import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Fn_FillListData, Fn_AddEditData, Fn_GetReport } from "../../store/Functions";
import { API_WEB_URLS } from "../../constants/constAPI";
import PageTitle from "../layouts/PageTitle";
import { toast } from "react-toastify";
import usePayrollParams from "../utils/usePayrollParams";
import {
  getCalculatedMonthDays,
  getWeeklyHolidayCount,
  reconstructAllowancesFromRecord,
  reconstructDeductionsFromRecord
} from "./MultiPayrollHelpers";

// ─── Compact inline styles (modern indigo palette) ──────────────────────────
const S = {
  page: { fontFamily: "'Poppins', sans-serif", fontSize: "12.5px", color: "#344767" },
  card: { border: "1px solid #E9ECEF", borderRadius: "10px", marginBottom: 0, background: "#ffffff", boxShadow: "0 2px 12px rgba(76,110,245,0.06), 0 1px 3px rgba(0,0,0,0.04)" },
  cardBody: { padding: "10px 14px" },
  label: { fontSize: "12px", fontWeight: 500, marginBottom: 0, whiteSpace: "nowrap", lineHeight: "28px", color: "#67748E" },
  input: { height: "28px", fontSize: "12px", padding: "2px 7px", borderRadius: "6px", border: "1px solid #E9ECEF", background: "#fff", color: "#344767", transition: "border-color 0.15s, box-shadow 0.15s" },
  inputRO: { height: "28px", fontSize: "12px", padding: "2px 7px", borderRadius: "6px", border: "1px solid #E9ECEF", background: "#F8FAFF", color: "#67748E" },
  select: { height: "28px", fontSize: "12px", padding: "0 7px", borderRadius: "6px", border: "1px solid #E9ECEF", background: "#fff", color: "#344767" },
  row: { display: "flex", alignItems: "center", marginBottom: "4px", gap: "6px" },
  tab: { cursor: "pointer", padding: "6px 16px", fontSize: "12px", fontWeight: 500, border: "none", borderBottom: "2px solid transparent", background: "transparent", marginRight: "2px", borderRadius: "0", userSelect: "none", color: "#67748E", transition: "color 0.15s, border-color 0.15s" },
  tabActive: { cursor: "pointer", padding: "6px 16px", fontSize: "12px", fontWeight: 600, border: "none", borderBottom: "2px solid #4C6EF5", background: "transparent", marginRight: "2px", borderRadius: "0", userSelect: "none", color: "#4C6EF5" },
  btn: { fontSize: "12px", padding: "5px 18px", fontWeight: 500, borderRadius: "6px", cursor: "pointer", border: "1px solid #E9ECEF", background: "#F8FAFF", color: "#344767", transition: "all 0.15s" },
  sectionLine: { borderBottom: "1px solid #E9ECEF", marginBottom: "6px", paddingBottom: "4px" },
  summaryLabel: { fontSize: "13px", fontWeight: 600, color: "#344767" },
  summaryVal: { fontSize: "13px", fontWeight: 700, color: "#344767", background: "rgba(76,110,245,0.08)", border: "1px solid rgba(76,110,245,0.2)", borderRadius: "6px", padding: "2px 10px", minWidth: "110px", textAlign: "right", display: "inline-block" },
};

const LF = ({ label, children, lw = 120 }) => (
  <div style={S.row}>
    <label style={{ ...S.label, width: lw, minWidth: lw, textAlign: "right", paddingRight: "4px" }}>{label}</label>
    {children}
  </div>
);

const to2Dec = (val) => {
  const num = parseFloat(val);
  return isNaN(num) ? "0.00" : num.toFixed(2);
};

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const getMonthIndex = (monthVal) => {
  if (monthVal === undefined || monthVal === null || monthVal === "") {
    return new Date().getMonth();
  }
  if (typeof monthVal === "string") {
    const idx = MONTH_NAMES.findIndex(m => m.toLowerCase() === monthVal.toLowerCase());
    if (idx >= 0) return idx;
    const num = parseInt(monthVal, 10);
    if (!isNaN(num) && num >= 1 && num <= 12) return num - 1;
  } else if (typeof monthVal === "number" && monthVal >= 1 && monthVal <= 12) {
    return monthVal - 1;
  }
  return new Date().getMonth();
};

// Helper to parse date into Date object (handles ISO YYYY-MM-DD, DD-MM-YYYY, DD/MM/YYYY, etc.)
const parseEffDate = (dateVal) => {
  if (!dateVal) return null;
  if (dateVal instanceof Date) return isNaN(dateVal.getTime()) ? null : dateVal;
  const s = String(dateVal).split("T")[0].split(" ")[0].trim();
  if (!s) return null;
  // YYYY-MM-DD or YYYY/MM/DD
  const m1 = s.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/);
  if (m1) {
    const y = parseInt(m1[1], 10);
    const m = parseInt(m1[2], 10) - 1;
    const d = parseInt(m1[3], 10);
    const dateObj = new Date(y, m, d);
    return isNaN(dateObj.getTime()) ? null : dateObj;
  }
  // DD-MM-YYYY or DD/MM/YYYY
  const m2 = s.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
  if (m2) {
    const d = parseInt(m2[1], 10);
    const m = parseInt(m2[2], 10) - 1;
    const y = parseInt(m2[3], 10);
    const dateObj = new Date(y, m, d);
    return isNaN(dateObj.getTime()) ? null : dateObj;
  }
  const direct = new Date(s);
  return isNaN(direct.getTime()) ? null : direct;
};

// ─── Allowance calculation helpers ─────────────────────────────────────────
// For each Allowance Master, pick the latest entry whose EffectiveFrom <= selected Month/Year (with fallback to latest)
const getEffectiveAllowances = (allowanceList, selectedMonth, selectedYear) => {
  if (!allowanceList || !Array.isArray(allowanceList) || allowanceList.length === 0) {
    return [];
  }

  const periodMonth = typeof selectedMonth === "number" ? selectedMonth : getMonthIndex(selectedMonth);
  const periodYear = parseInt(selectedYear, 10) || 0;

  // Group all entries by Allowance Master ID
  const groups = {};
  allowanceList.forEach((a, idx) => {
    if (!a) return;
    const masterId = a.F_AllowanceMaster || a.AllowanceMasterId || a.Id;
    if (!masterId || masterId === "none") return;
    const key = String(masterId);
    if (!groups[key]) groups[key] = [];
    groups[key].push({ ...a, _origIndex: idx });
  });

  const sortLatestFirst = (list) => {
    return [...list].sort((a, b) => {
      const dateA = a.EffectiveFrom || a.EffectiveDate || a.Date || "";
      const dateB = b.EffectiveFrom || b.EffectiveDate || b.Date || "";
      const dA = parseEffDate(dateA);
      const dB = parseEffDate(dateB);
      const tA = dA ? dA.getTime() : 0;
      const tB = dB ? dB.getTime() : 0;
      if (tA !== tB) {
        return tB - tA; // Latest date first
      }
      return (b._origIndex ?? 0) - (a._origIndex ?? 0); // Highest index first
    });
  };

  const result = [];

  Object.values(groups).forEach(entries => {
    if (entries.length === 0) return;

    if (periodYear > 0 && periodMonth >= 0) {
      const eligible = entries.filter(e => {
        const dateVal = e.EffectiveFrom || e.EffectiveDate || e.Date || "";
        const d = parseEffDate(dateVal);
        if (!d) return true;
        const y = d.getFullYear();
        const m = d.getMonth();
        return (y < periodYear) || (y === periodYear && m <= periodMonth);
      });

      if (eligible.length > 0) {
        const sortedEligible = sortLatestFirst(eligible);
        result.push(sortedEligible[0]);
        return;
      }
    }

    // Fallback: Pick latest version by EffectiveFrom date / last entry
    const sortedAll = sortLatestFirst(entries);
    result.push(sortedAll[0]);
  });

  return result;
};

// Compute a single allowance's effective amount based on Calculation type and WorkingDaysApply
const computeSingleAllowance = (a, monthDays, paidDays, basicSalary) => {
  if (!a) return 0;
  if (a.IsApplicable === false || a.IsApplicable === "false" || a.IsApplicable === 0 || a.IsApplicable === "0") return 0;
  let base = parseFloat(a.Amount) || 0;
  const calc = String(a.Calculation || "").toUpperCase().trim();
  
  if (calc === "PERCENTAGE" || calc === "PERCENT" || calc === "%") {
    base = (base / 100) * (parseFloat(basicSalary) || 0);
  } else if (calc === "DAILY" || calc === "PER DAY" || calc === "PERDAY") {
    return Math.round(base * (parseFloat(paidDays) || 0) * 100) / 100;
  }
  
  const isWorkDays = a.IsWorkingDaysApply === true || a.IsWorkingDaysApply === "true" || a.IsWorkingDaysApply === 1;
  const mDays = parseFloat(monthDays) || 0;
  const pDays = parseFloat(paidDays) || 0;
  if (isWorkDays && mDays > 0) {
    base = (base / mDays) * pDays;
  }
  
  return Math.round(base * 100) / 100;
};

// MANUAL / DAILY / PERCENTAGE → Amount (with proper calculation logic)
const computeAllowancesTotal = (allowanceList, monthDays, paidDays, basicSalary, selectedMonth, selectedYear) => {
  const effective = getEffectiveAllowances(allowanceList, selectedMonth, selectedYear);
  let total = 0;
  effective.forEach(a => {
    total += computeSingleAllowance(a, monthDays, paidDays, basicSalary);
  });
  return Math.round(total * 100) / 100;
};

// Sum of computed amounts for allowances where IsPFApply = true → added to PF wage base
const computePFAllowancesAmount = (allowanceList, monthDays, paidDays, basicSalary, selectedMonth, selectedYear) => {
  const effective = getEffectiveAllowances(allowanceList, selectedMonth, selectedYear);
  let total = 0;
  effective.forEach(a => {
    const isPF = a.IsPFApply === true || a.IsPFApply === "true" || a.IsPFApply === 1;
    if (isPF) {
      total += computeSingleAllowance(a, monthDays, paidDays, basicSalary);
    }
  });
  return Math.round(total * 100) / 100;
};

// Sum of computed amounts for allowances where IsESIApply = true → used as ESI wage base
const computeESIAllowancesAmount = (allowanceList, monthDays, paidDays, basicSalary, selectedMonth, selectedYear) => {
  const effective = getEffectiveAllowances(allowanceList, selectedMonth, selectedYear);
  let total = 0;
  effective.forEach(a => {
    const isESI = a.IsESIApply === true || a.IsESIApply === "true" || a.IsESIApply === 1;
    if (isESI) {
      total += computeSingleAllowance(a, monthDays, paidDays, basicSalary);
    }
  });
  return Math.round(total * 100) / 100;
};

// ─── Deduction calculation helpers (same pattern as Allowances) ─────────────
const getEffectiveDeductions = (deductionList, selectedMonth, selectedYear) => {
  if (!deductionList || !Array.isArray(deductionList) || deductionList.length === 0) {
    return [];
  }

  const periodMonth = typeof selectedMonth === "number" ? selectedMonth : getMonthIndex(selectedMonth);
  const periodYear = parseInt(selectedYear, 10) || 0;

  // Group all entries by Deduction Master ID
  const groups = {};
  deductionList.forEach((d, idx) => {
    if (!d) return;
    const masterId = d.F_DeductionMaster || d.DeductionMasterId || d.Id;
    if (!masterId || masterId === "none") return;
    const key = String(masterId);
    if (!groups[key]) groups[key] = [];
    groups[key].push({ ...d, _origIndex: idx });
  });

  const sortLatestFirst = (list) => {
    return [...list].sort((a, b) => {
      const dateA = a.EffectiveFrom || a.EffectiveDate || a.Date || "";
      const dateB = b.EffectiveFrom || b.EffectiveDate || b.Date || "";
      const dA = parseEffDate(dateA);
      const dB = parseEffDate(dateB);
      const tA = dA ? dA.getTime() : 0;
      const tB = dB ? dB.getTime() : 0;
      if (tA !== tB) {
        return tB - tA; // Latest date first
      }
      return (b._origIndex ?? 0) - (a._origIndex ?? 0); // Highest index first
    });
  };

  const result = [];

  Object.values(groups).forEach(entries => {
    if (entries.length === 0) return;

    if (periodYear > 0 && periodMonth >= 0) {
      const eligible = entries.filter(e => {
        const dateVal = e.EffectiveFrom || e.EffectiveDate || e.Date || "";
        const dt = parseEffDate(dateVal);
        if (!dt) return true;
        const y = dt.getFullYear();
        const m = dt.getMonth();
        return (y < periodYear) || (y === periodYear && m <= periodMonth);
      });

      if (eligible.length > 0) {
        const sortedEligible = sortLatestFirst(eligible);
        result.push(sortedEligible[0]);
        return;
      }
    }

    // Fallback: Pick latest version by EffectiveFrom date / last entry
    const sortedAll = sortLatestFirst(entries);
    result.push(sortedAll[0]);
  });

  return result;
};

const computeSingleDeduction = (d, monthDays, paidDays, basicSalary) => {
  if (!d) return 0;
  if (d.IsApplicable === false || d.IsApplicable === "false" || d.IsApplicable === 0 || d.IsApplicable === "0") return 0;
  let base = parseFloat(d.Amount) || 0;
  const calc = String(d.Calculation || "").toUpperCase().trim();
  if (calc === "PERCENTAGE" || calc === "PERCENT" || calc === "%") {
    base = (base / 100) * (parseFloat(basicSalary) || 0);
  } else if (calc === "DAILY" || calc === "PER DAY" || calc === "PERDAY") {
    return Math.round(base * (parseFloat(paidDays) || 0) * 100) / 100;
  }
  const isWorkDays = d.IsWorkingDaysApply === true || d.IsWorkingDaysApply === "true" || d.IsWorkingDaysApply === 1;
  const mDays = parseFloat(monthDays) || 0;
  const pDays = parseFloat(paidDays) || 0;
  if (isWorkDays && mDays > 0) {
    base = (base / mDays) * pDays;
  }
  return Math.round(base * 100) / 100;
};

const computeDeductionsTotal = (deductionList, monthDays, paidDays, basicSalary, selectedMonth, selectedYear) => {
  const effective = getEffectiveDeductions(deductionList, selectedMonth, selectedYear);
  let total = 0;
  effective.forEach(d => {
    total += computeSingleDeduction(d, monthDays, paidDays, basicSalary);
  });
  return Math.round(total * 100) / 100;
};

// Pick latest SalaryJson entry whose EffectiveDate <= selected Month/Year  
const getEffectiveSalary = (salaryList, selectedMonth, selectedYear) => {
  const periodMonth = getMonthIndex(selectedMonth);
  const periodYear = parseInt(selectedYear, 10) || new Date().getFullYear();
  
  const eligible = (salaryList || []).filter(s => {
    const effDate = new Date(s.EffectiveDate);
    const effYear = effDate.getFullYear();
    const effMonth = effDate.getMonth();
    return (effYear < periodYear) || (effYear === periodYear && effMonth <= periodMonth);
  });
  
  const pool = eligible.length > 0 ? eligible : (salaryList || []);
  return pool.reduce((best, s) => !best || new Date(s.EffectiveDate) > new Date(best.EffectiveDate) ? s : best, null);
};

// Compute Overtime amount from OvertimeHours using BasicType and payrollParams
// Monthly(1): basicSalary / divisorDays / 8 * multiplier * otHours
// Per Day(2): basicSalary / 8 * multiplier * otHours  (basicSalary is daily rate)
// Hourly(3):  basicSalary * multiplier * otHours       (basicSalary is already per-hour rate)
const computeOvertime = (salaryEntry, otHours, monthDays, payrollParams) => {
  if (!payrollParams || !payrollParams.IsOvertime) { return 0; }
  if (!salaryEntry) { return 0; }
  const hours = parseFloat(otHours) || 0;
  if (hours <= 0) { return 0; }
  const basicSalary = parseFloat(salaryEntry.BasicSalary) || 0;
  if (basicSalary <= 0) { return 0; }
  const type       = salaryEntry.F_BasicTypeMaster;
  const divisor    = payrollParams.IsOTOn26Days ? 26 : (monthDays || 26);
  const multiplier = payrollParams.IsDoubleOT ? 2 : 1;
  let hourlyRate = 0;
  if (type === 2) {
    // Per Day: daily rate ÷ 8 hrs
    hourlyRate = basicSalary / 8;
  } else if (type === 3) {
    // Hourly: already per-hour
    hourlyRate = basicSalary;
  } else {
    // Monthly (type=1 or default): basicSalary ÷ divisorDays ÷ 8
    hourlyRate = basicSalary / divisor / 8;
  }
  const result = Math.round(hourlyRate * multiplier * hours * 100) / 100;
  return result;
};

// Calculate working days based on PayrollParameter settings
const computeWorkingDays = (monthDays, payrollParams) => {
  if (!payrollParams) return 26; // default
  
  // If 26 working days is enabled
  if (payrollParams.Is26DaysWorking) return 26;
  
  // If Sunday Include is enabled, include all days
  if (payrollParams.IsSundayInclude) return monthDays;
  
  // Default: exclude approx 4-5 Sundays
  const sundays = Math.floor(monthDays / 7);
  return monthDays - sundays;
};

// Get working hours/minutes display for hourly payment
const getWorkingHoursDisplay = (workingHours, workingMinutes, payrollParams) => {
  if (!payrollParams || !payrollParams.IsHourlyPayment) return "";
  
  const hours = parseInt(workingHours) || 0;
  const minutes = parseInt(workingMinutes) || 0;
  
  // Only show if values are set
  if (hours === 0 && minutes === 0) return "";
  
  return `${hours}h ${minutes}m`;
};

// F_BasicTypeMaster: 1=Monthly (as-is), 2=Per Day (*WorkingDays), 3=Hourly (*WorkingDays*8)
const computeBasic = (salaryEntry, workingDays, payrollParams) => {
  if (!salaryEntry) return 0;
  const raw  = parseFloat(salaryEntry.BasicSalary) || 0;
  const type = salaryEntry.F_BasicTypeMaster;
  
  // Monthly: use as-is
  if (type === 1) return raw;
  
  // Per Day: multiply by working days
  if (type === 2) return Math.round(raw * workingDays * 100) / 100;
  
  // Hourly: multiply by working days * hours per day
  if (type === 3) {
    const hoursPerDay = payrollParams && payrollParams.IsHourlyPayment ? 8 : 8; // default 8 hours
    return Math.round(raw * workingDays * hoursPerDay * 100) / 100;
  }
  
  return raw;
};

// Wages calculation based on PaidDays and BasicType
const computeWages = (salaryEntry, paidDays, monthDays, workingHours = 0, workingMinutes = 0, payrollParams) => {
  if (!salaryEntry) return 0;
  const basicSalary = parseFloat(salaryEntry.BasicSalary) || 0;
  const type = salaryEntry.F_BasicTypeMaster;
  
  // Monthly: BasicSalary * (PaidDays / MonthDays)
  if (type === 1) {
    if (monthDays === 0) return 0;
    return Math.round((basicSalary * paidDays / monthDays) * 100) / 100;
  }
  
  // Per Day: BasicSalary * PaidDays
  if (type === 2) {
    return Math.round(basicSalary * paidDays * 100) / 100;
  }
  
  // Hourly: BasicSalary * (WorkingHours + WorkingMinutes/60) * PaidDays
  if (type === 3) {
    const totalHours = workingHours + (workingMinutes / 60);
    return Math.round(basicSalary * totalHours * paidDays * 100) / 100;
  }
  
  return basicSalary;
};

const MONTH_NAMES_PY = ["January","February","March","April","May","June","July","August","September","October","November","December"];

const getInitialDynFields = () => {
  const fields = {};
  for (let i = 1; i <= 10; i++) {
    fields[`Allowance${i}Name`] = "";
    fields[`OriginalAllowance${i}`] = 0;
    fields[`CalculatedAllowance${i}`] = 0;
    fields[`Deduction${i}Name`] = "";
    fields[`OriginalDeduction${i}`] = 0;
    fields[`CalculatedDeduction${i}`] = 0;
  }
  return fields;
};

const Payroll = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState("entry");

  // ── Session: Firm / Month / Year from localStorage (set in Home/Dashboard) ──
  const readSession = () => ({
    firm: (() => { try { return JSON.parse(localStorage.getItem("selectedFirm") || "null"); } catch { return null; } })(),
    month: localStorage.getItem("selectedPayrollMonth") || "",
    year:  parseInt(localStorage.getItem("selectedPayrollYear") || "0"),
  });
  const [sessionFirm,  setSessionFirm]  = useState(() => readSession().firm);
  const [selectedMonth, setSelectedMonth] = useState(() => readSession().month);
  const [selectedYear,  setSelectedYear]  = useState(() => readSession().year);

  // Sync when another tab or Home changes localStorage
  useEffect(() => {
    const onStorage = () => {
      const s = readSession();
      setSessionFirm(s.firm);
      setSelectedMonth(s.month);
      setSelectedYear(s.year);
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isSessionReady = !!sessionFirm?.Id && !!selectedMonth && !!selectedYear;

  const [state, setState] = useState({ employees: [], selectedEmployee: null, isLoading: false });
  const [rawAllowances, setRawAllowances] = useState([]);
  const [rawDeductions, setRawDeductions] = useState([]);
  const [allowanceMasterList, setAllowanceMasterList] = useState([]);
  const [deductionMasterList, setDeductionMasterList] = useState([]);
  const [leaveMasterList, setLeaveMasterList] = useState([]); // LeaveMaster from firm
  const [leaveValues, setLeaveValues] = useState({}); // {leaveId: days}
  const [pfRate, setPfRate] = useState(null);
  const [esiRate, setEsiRate] = useState(null);
  const [bonusRate, setBonusRate] = useState(null);
  const [gratuityRate, setGratuityRate] = useState(null);
  const [payrollParams, setPayrollParams] = useState(null);
  const [basicTypeMaster, setBasicTypeMaster] = useState([]);

  const [lastField, setLastField] = React.useState(null);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [savedRecords, setSavedRecords] = useState([]);
  const [selectedRecordId, setSelectedRecordId] = useState(null);
  const [showSidePanel, setShowSidePanel] = useState(false);
  const [panelSearch, setPanelSearch] = useState("");
  const empSelectRef = React.useRef(null);

  // ── Multi ESI / Multi PF filters ─────────────────────────────────────────
  const { isMultiESI, isMultiPF, esiNoList, pfNoList } = usePayrollParams();
  const [filterEsiNo, setFilterEsiNo] = useState(""); // ESI No. filter for employee dropdown
  const [filterPfNo,  setFilterPfNo]  = useState(""); // PF No. filter for employee dropdown
  const [f, setF] = useState({
    F_EmployeeMaster: "", EmployeeName: "", FatherName: "", Department: "", JoinDate: "",
    BasicSalary: 0, HRA: 0, Conveyance: 0, CCA: 0, Medical: 0, Retention: 0,
    MonthDays: 31,
    WorkingDays: 0, WorkingHours: 0, WorkingMinutes: 0, LeaveWages: 0, FestivalHoliday: 0, WeeklyHoliday: 0, OvertimeHours: 0,
    Arrear: 0, AdvanceBonus: 0, ExtraPay: 0, ExtraPay1: 0,
    PFRefund: 0, Recovery: 0, Advance: 0, TDS: 0,
    PFReason: "", PFLast: "",
    PL_Bal: 0, CL_Bal: 0, ML_Bal: 0, PL_Enjoy: 0, CL_Enjoy: 0, ML_Enjoy: 0, // CL_Bal/ML_Bal mapped to Leave1/Leave2 balance
    Wages: 0, OverTime: 0, EmplrPF: 0, Pension: 0, PF: 0, ArrearPF: 0, ArrERPF: 0, ArrPens: 0, EmplrESI: 0, ESI: 0,
    Bonus: 0, Gratuity: 0, // Employer-paid amounts (Bonus, Gratuity not deducted)
    AdminCharges: 0, AC21: 0, AC22: 0, // Employer-only PF charges (NOT deducted from employee)
    PFApplicable: false, ESIApplicable: false,
    PaidDay: 0,
    TotalAllowances: 0, TotalDeductions: 0, GrossAmount: 0, TotalDeduction: 0, NetAmount: 0,
    LeaveWithWages: 0,
    LeaveFrom1: "", LeaveTo1: "", LeaveFrom2: "", LeaveTo2: "", LeaveFrom3: "", LeaveTo3: "",
    AbsentDays: 0,
    AbsentFrom1: "", AbsentTo1: "", AbsentFrom2: "", AbsentTo2: "", AbsentFrom3: "", AbsentTo3: "",
    BasicTypeName: "", WorkingHoursDisplay: "", // New fields
    ...getInitialDynFields(),
  });

  const set = React.useCallback((k, v) => {
    setLastField(k);
    setF(p => ({ ...p, [k]: v }));
  }, []);
  const setNum = React.useCallback((k, v) => {
    setLastField(k);
    if (v === "") {
      setF(p => ({ ...p, [k]: "" }));
      return;
    }
    const numericVal = parseFloat(v) || 0;
    setF(p => {
      const monthDays = parseFloat(p.MonthDays) || 0;
      const workingDays = parseFloat(p.WorkingDays) || 0;
      if (k === 'WorkingDays' && numericVal > monthDays) {
        toast.error(`Working Days (${numericVal}) cannot exceed Month Days (${monthDays})`);
        return { ...p, WorkingDays: 0 };
      }
      if (k === 'MonthDays' && numericVal < workingDays) {
        toast.error(`Month Days (${numericVal}) cannot be less than Working Days (${workingDays})`);
        return { ...p, WorkingDays: 0, MonthDays: numericVal };
      }
      return { ...p, [k]: numericVal };
    });
  }, []);

  // Log payroll session info on page load
  const logPayrollSession = async () => {
    const monthNum = getMonthIndex(selectedMonth) + 1;
    const monthStr = String(monthNum).padStart(2, '0');
    console.log(`📋 Payroll Session Loaded: Firm ID = ${sessionFirm?.Id || 0}, Month = ${monthStr}, Year = ${selectedYear}`);
    try {
      const formData = new FormData();
      formData.append("Id", 0);
      formData.append("F_FirmMaster", sessionFirm?.Id || 0);
      formData.append("PayrollMonth", monthStr);
      formData.append("PayrollYear", selectedYear);
      const res = await Fn_GetReport(
        dispatch,
        setState,
        "data",
        'GetPayrollTransaction/0/token',
        { arguList: { id: 0, formData: formData } },
        true
      );
      if (res && Array.isArray(res)) {
        setSavedRecords(res);
        console.log(`✅ Loaded ${res.length} payroll record(s)`);
      }
    } catch (err) {
      console.error("Error loading payroll records:", err);
    }
  };

  useEffect(() => {
    logPayrollSession();
  }, []);

  useEffect(() => {
    const firmId = JSON.parse(localStorage.getItem("selectedFirm") || "{}").Id || 0;
    loadEmployees(firmId);
    loadPayrollParams(firmId);
    loadBasicTypeMaster();
    Fn_FillListData(dispatch, () => {}, "Data", `${API_WEB_URLS.MASTER}/0/token/AllowanceMaster/Id/0`)
      .then(data => setAllowanceMasterList(data || []))
      .catch(() => {});
    Fn_FillListData(dispatch, () => {}, "Data", `${API_WEB_URLS.MASTER}/0/token/DeductionMaster/Id/0`)
      .then(data => setDeductionMasterList(data || []))
      .catch(() => {});
    // Load LeaveMaster for this firm (same as PageList_LeaveMaster)
    Fn_FillListData(dispatch, () => {}, "Data", `${API_WEB_URLS.MASTER}/0/token/LeaveMaster/TBL.F_FirmMaster/${firmId}`)
      .then(data => {
        setLeaveMasterList(data || []);
        // Init leaveValues with 0 for each leave
        const init = {};
        (data || []).forEach(l => { init[l.Id] = 0; });
        setLeaveValues(init);
      })
      .catch(() => {});
    loadPfRate(firmId);
    loadEsiRate(firmId);
    loadBonusRate(firmId);
    loadGratuityRate(firmId);
  }, []);

  const loadEmployees = async (firmId) => {
    try {
      setState(p => ({ ...p, isLoading: true }));
      if (!firmId) firmId = JSON.parse(localStorage.getItem("selectedFirm") || "{}").Id || 0;
      const data = await Fn_FillListData(dispatch, () => {}, "Data", `${API_WEB_URLS.MASTER}/0/token/EmployeeDataDynamic/0/${firmId}`);
      setState(p => ({ ...p, employees: data || [], isLoading: false }));
    } catch { setState(p => ({ ...p, isLoading: false })); }
  };

  const loadPfRate = async (firmId) => {
    try {
      if (!firmId) firmId = JSON.parse(localStorage.getItem("selectedFirm") || "{}").Id || 0;
      const res = await Fn_FillListData(dispatch, () => {}, "Data", `${API_WEB_URLS.MASTER}/0/token/PfParamFetch/TBL.F_FirmMaster/${firmId}`);
      const sorted = (res || []).sort((a, b) => new Date(a.PFDate) - new Date(b.PFDate));
      setPfRate(sorted.length > 0 ? sorted[sorted.length - 1] : null);
    } catch { setPfRate(null); }
  };

  const loadEsiRate = async (firmId) => {
    try {
      if (!firmId) firmId = JSON.parse(localStorage.getItem("selectedFirm") || "{}").Id || 0;
      const res = await Fn_FillListData(dispatch, () => {}, "Data", `${API_WEB_URLS.MASTER}/0/token/EsiParamFetch/TBL.F_FirmMaster/${firmId}`);
      const sorted = (res || []).sort((a, b) => new Date(a.ESIDate) - new Date(b.ESIDate));
      setEsiRate(sorted.length > 0 ? sorted[sorted.length - 1] : null);
    } catch { setEsiRate(null); }
  };

  const loadBonusRate = async (firmId) => {
    try {
      if (!firmId) firmId = JSON.parse(localStorage.getItem("selectedFirm") || "{}").Id || 0;
      const res = await Fn_FillListData(dispatch, () => {}, "Data", `${API_WEB_URLS.MASTER}/0/token/BonusParamFetch/TBL.F_FirmMaster/${firmId}`);
      const sorted = (res || []).sort((a, b) => new Date(a.BonusDate) - new Date(b.BonusDate));
      setBonusRate(sorted.length > 0 ? sorted[sorted.length - 1] : null);
    } catch { setBonusRate(null); }
  };

  const loadGratuityRate = async (firmId) => {
    try {
      if (!firmId) firmId = JSON.parse(localStorage.getItem("selectedFirm") || "{}").Id || 0;
      const res = await Fn_FillListData(dispatch, () => {}, "Data", `${API_WEB_URLS.MASTER}/0/token/GratuityParamFetch/TBL.F_FirmMaster/${firmId}`);
      const sorted = (res || []).sort((a, b) => new Date(a.GratuityDate) - new Date(b.GratuityDate));
      setGratuityRate(sorted.length > 0 ? sorted[sorted.length - 1] : null);
    } catch { setGratuityRate(null); }
  };

  const loadPayrollParams = async (firmId) => {
    try {
      if (!firmId) firmId = JSON.parse(localStorage.getItem("selectedFirm") || "{}").Id || 0;
      const res = await Fn_FillListData(dispatch, () => {}, "Data", `${API_WEB_URLS.MASTER}/0/token/PayrollParameter/TBL.F_FirmMaster/${firmId}`);
      setPayrollParams((res && Array.isArray(res) && res.length > 0) ? res[0] : null);
    } catch { setPayrollParams(null); }
  };

  const loadBasicTypeMaster = async () => {
    try {
      const res = await Fn_FillListData(dispatch, () => {}, "Data", `${API_WEB_URLS.MASTER}/0/token/BasicTypeMaster/Id/0`);
      setBasicTypeMaster(res || []);
    } catch { setBasicTypeMaster([]); }
  };

  // ESI computation:
  // - If gross > WagesLimit → employee is above ESI ceiling → ESI = 0, EmplrESI = 0
  // - EmployeeExemptionLimit: if gross <= ExemptionLimit → employee contribution = 0 (employer still contributes)
  // - RoundedOn: comes from PayrollParameter (rounds to nearest integer)
  const computeESI = (grossWages, esi, pparms) => {
    if (!esi) return { ESI: 0, EmplrESI: 0 };
    const wagesLimit      = parseFloat(esi.WagesLimit)              || 0;
    const empRate         = parseFloat(esi.EmployeeContribution)    || 0;
    const erRate          = parseFloat(esi.EmployerContribution)    || 0;
    const exemptionLimit  = parseFloat(esi.EmployeeExemptionLimit)  || 0;
    const roundOn         = !!(esi?.RoundedOn);
    // Above wage ceiling → no ESI
    if (wagesLimit > 0 && grossWages > wagesLimit) return { ESI: 0, EmplrESI: 0 };
    let empESI  = grossWages * empRate  / 100;
    let emplrESI = grossWages * erRate  / 100;
    // Below employee exemption limit → employee part waived
    if (exemptionLimit > 0 && grossWages <= exemptionLimit) empESI = 0;
    if (roundOn) { empESI = Math.round(empESI); emplrESI = Math.round(emplrESI); }
    else { empESI = Math.round(empESI * 100) / 100; emplrESI = Math.round(emplrESI * 100) / 100; }
    return { ESI: empESI, EmplrESI: emplrESI };
  };

  // ─── PF / Pension / EmplrPF computation ───────────────────────────────────
  // pfBase       = BasicSalary + allowances where IsPFApply=true
  // pfWages      = min(pfBase, MaxWagesForPF)  when MaxWagesForPF > 0
  // Employee PF  = pfWages * Rate / 100
  // Employer PF  = pfWages * (EmployerRate − PensionRate) / 100
  // Pension      = min(BasicSalary, MaxWagesForPension) * PensionRate/100  capped at MaxPensionAmt
  const computePF = (basicSalary, pf, pfAllowancesAmount = 0, isPensionApplicable = true) => {
    if (!pf) return { PF: 0, EmplrPF: 0, Pension: 0, AdminCharges: 0, AC21: 0, AC22: 0 };
    const rate        = parseFloat(pf.Rate)              || 0;
    const erRate      = parseFloat(pf.EmployerRate)      || 0;
    const penRate     = parseFloat(pf.PensionRate)       || 0;
    const maxPen      = parseFloat(pf.MaxPensionAmt)     || 0;
    const maxWagesPF  = parseFloat(pf.MaxWagesForPF)     || 0;
    const maxWagesPen = parseFloat(pf.MaxWagesForPension)|| 0;
    const pfBase      = basicSalary + (pfAllowancesAmount || 0);
    const pfWages     = maxWagesPF  > 0 ? Math.min(pfBase, maxWagesPF)  : pfBase;
    const penWages    = maxWagesPen > 0 ? Math.min(basicSalary, maxWagesPen) : basicSalary;
    const empPF       = Math.round(pfWages  * rate              / 100);
    const penApply    = isPensionApplicable !== false && isPensionApplicable !== 0 && String(isPensionApplicable).toLowerCase() !== "false";
    let   pension     = penApply ? Math.round(penWages * penRate / 100) : 0;
    if (maxPen > 0 && penApply) pension = Math.min(pension, maxPen);
    const actualPenRate = penApply ? penRate : 0;
    const emplrPF     = Math.round(pfWages  * Math.max(erRate - actualPenRate, 0) / 100);
    // ── Admin Charges (A/C 22) — Employer only, NOT deducted from employee salary ──
    // max(pfWages * AdminCharges%, AdminChargesMinAmount)
    const adminRate   = parseFloat(pf.AdminCharges)           || 0;
    const adminMin    = parseFloat(pf.AdminChargesMinAmount)  || 0;
    const adminCalc   = Math.round(pfWages * adminRate / 100 * 100) / 100;
    const AdminCharges = Math.max(adminCalc, adminMin);
    // ── AC21 (EDLI / Group Ins) — Employer only ──
    const ac21WithGrp    = parseFloat(pf.AC21WithGroupIns)         || 0;
    const ac21WithoutGrp = parseFloat(pf.AC21WithoutGroupIns)      || 0;
    const ac21MinAmt     = parseFloat(pf.AC21WithGroupInsMinAmt)   || 0;
    const ac21Rate       = ac21WithGrp > 0 ? ac21WithGrp : ac21WithoutGrp;
    const ac21Calc       = Math.round(pfWages * ac21Rate / 100 * 100) / 100;
    const AC21           = Math.max(ac21Calc, ac21MinAmt);
    // ── AC22 — Employer only ──
    const ac22WithGrp    = parseFloat(pf.AC22WithGroupIns)         || 0;
    const ac22WithoutGrp = parseFloat(pf.AC22WithoutGroupIns)      || 0;
    const ac22MinAmt     = parseFloat(pf.AC22WithGroupInsMinAmt)   || 0;
    const ac22Rate       = ac22WithGrp > 0 ? ac22WithGrp : ac22WithoutGrp;
    const ac22Calc       = Math.round(pfWages * ac22Rate / 100 * 100) / 100;
    const AC22           = ac22Rate > 0 ? Math.max(ac22Calc, ac22MinAmt) : 0;
    return { PF: empPF, EmplrPF: emplrPF, Pension: pension, AdminCharges, AC21, AC22 };
  };

  // ─── Bonus computation ─────────────────────────────────────────────────────
  // Bonus = (wages * BonusRate / 100) or flat amount based on configuration
  // MinimumBonusDays: minimum working days required for bonus eligibility
  const computeBonus = (wages, bonus, workingDays) => {
    if (!bonus) return 0;
    const rate = parseFloat(bonus.BonusRate) || 0;
    const maxWages = parseFloat(bonus.MaximumWages) || 0;
    const maxBonus = parseFloat(bonus.MaximumBonus) || 0;
    const minBonus = parseFloat(bonus.MinimumBonus) || 0;
    const minDays = parseFloat(bonus.MinimumBonusDays) || 0;
    
    // Check minimum working days eligibility
    if (minDays > 0 && workingDays < minDays) return 0;
    
    // Calculate wages for bonus (capped at MaximumWages if specified)
    const bonusWages = maxWages > 0 ? Math.min(wages, maxWages) : wages;
    let bonusAmount = bonusWages * rate / 100;
    
    // Apply maximum and minimum bonus limits
    if (maxBonus > 0) bonusAmount = Math.min(bonusAmount, maxBonus);
    if (minBonus > 0) bonusAmount = Math.max(bonusAmount, minBonus);
    
    return Math.round(bonusAmount * 100) / 100;
  };

  // ─── Gratuity computation ───────────────────────────────────────────────────
  // Gratuity = (LastDrawnBasic * 15 * YearsOfService) / 26  or Rate-based calculation
  const computeGratuity = (basicSalary, gratuity, yearsOfService) => {
    if (!gratuity) return 0;
    const rate = parseFloat(gratuity.Rate) || 0;
    
    // If rate is provided, use rate-based calculation
    if (rate > 0) {
      return Math.round(basicSalary * rate / 100 * 100) / 100;
    }
    
    // Default gratuity calculation: (Basic * 15 * YearsOfService) / 26
    // For now, using a simple rate-based approach since we don't have service years
    return Math.round(basicSalary * 4.81 / 100 * 100) / 100; // Approximation
  };

  const handleEmployeeChange = async (id) => {
    if (!id) { 
      set('F_EmployeeMaster', ''); set('EmployeeName', ''); set('FatherName', ''); set('Department', ''); 
      set('BasicTypeName', ''); set('WorkingHoursDisplay', '');
      setRawAllowances([]);
      setRawDeductions([]);
      setSelectedRecordId(null); // Clear edit mode
      return; 
    }

    // Check if employee already has a saved record for this month/year (unless editing)
    if (selectedRecordId === null) {
      const exists = savedRecords.some(r => String(r.F_EmployeeMaster) === String(id));
      if (exists) {
        toast.error("Payroll record for this employee already exists in the selected month!");
        set('F_EmployeeMaster', ''); set('EmployeeName', ''); set('FatherName', ''); set('Department', ''); 
        set('BasicTypeName', ''); set('WorkingHoursDisplay', '');
        setRawAllowances([]);
        setRawDeductions([]);
        return;
      }
    }

    const emp = state.employees.find(e => String(e.Id) === String(id));
    if (!emp) return;
    let al = [];
    try { al = emp.AllowanceJson ? JSON.parse(emp.AllowanceJson) : []; } catch {}
    let dl = [];
    try { dl = emp.DeductionJson ? JSON.parse(emp.DeductionJson) : []; } catch {}
    let salList = [];
    try { salList = emp.SalaryJson ? JSON.parse(emp.SalaryJson) : []; } catch {}
    const effAl = getEffectiveAllowances(al, selectedMonth, selectedYear);
    const effDl = getEffectiveDeductions(dl, selectedMonth, selectedYear);
    setRawAllowances(effAl);
    setRawDeductions(effDl);
    setF(p => {
      const salEntry     = getEffectiveSalary(salList, selectedMonth, selectedYear);
      const calculatedMonthDays = getCalculatedMonthDays(selectedMonth, selectedYear, payrollParams, emp.WeeklyHoliday);
      const workingDays  = computeWorkingDays(calculatedMonthDays, payrollParams);
      // monthlyBasic = monthly equivalent used for % allowance bases (e.g. 500/day → 500*26=13000)
      const monthlyBasic = computeBasic(salEntry, workingDays, payrollParams);
      const totalLeave = Object.values(leaveValues).reduce((s, v) => s + (parseFloat(v) || 0), 0);
      const paidDays     = Math.min(calculatedMonthDays, (parseFloat(p.WorkingDays) || 0) + (parseFloat(p.LeaveWages) || 0) + (parseFloat(p.FestivalHoliday) || 0) + (parseFloat(p.WeeklyHoliday) || 0) + totalLeave);
      // wages = actual earned wages based on BasicType + paid days
      const wages        = computeWages(salEntry, paidDays, calculatedMonthDays, p.WorkingHours, p.WorkingMinutes, payrollParams);
      const totalAl      = computeAllowancesTotal(effAl, calculatedMonthDays, paidDays, monthlyBasic, selectedMonth, selectedYear);
      const totalDed     = computeDeductionsTotal(effDl, calculatedMonthDays, paidDays, monthlyBasic, selectedMonth, selectedYear);
      
      // Get BasicType name
      const basicType = basicTypeMaster.find(bt => bt.Id === salEntry?.F_BasicTypeMaster);
      const basicTypeName = basicType ? basicType.Name : "";
      
      // Get working hours display for hourly payment
      const workingHoursDisplay = getWorkingHoursDisplay(p.WorkingHours, p.WorkingMinutes, payrollParams);
      
      return {
        ...p,
        F_EmployeeMaster: id,
        EmployeeName: emp.Name || "",
        FatherName: emp.FatherName || "",
        Department: emp.DepartmentName || emp.Department || "",
        JoinDate: emp.JoinDate ? String(emp.JoinDate).substring(0, 10) : "",
        MonthDays: calculatedMonthDays,
        WeeklyHoliday: 0,
        // Show raw rate from master: 500 for daily, 13000 for monthly — as-is
        BasicSalary: parseFloat(salEntry?.BasicSalary) || 0,
        Wages: wages,
        TotalAllowances: totalAl,
        TotalDeductions: totalDed,
        BasicTypeName: basicTypeName,
        WorkingHoursDisplay: workingHoursDisplay,
        PFApplicable: emp.PFApplicable || false,
        ESIApplicable: emp.ESIApplicable || false,
        PensionApplicable: emp.PensionApplicable !== undefined ? !!emp.PensionApplicable : true,
      };
    });
    setState(p => ({ ...p, selectedEmployee: emp }));
  };

  // Load saved record into form
  const loadSavedRecord = (record) => {
    if (!record || !record.F_EmployeeMaster) return;
    
    // Find employee and fill basic info
    const emp = state.employees.find(e => String(e.Id) === String(record.F_EmployeeMaster));
    
    setSelectedRecordId(record.Id); // Mark as edit mode

    // ── Restore rawAllowances & rawDeductions ──
    let al = [];
    let dl = [];
    if (emp) {
      try { al = emp.AllowanceJson ? JSON.parse(emp.AllowanceJson) : []; } catch {}
      try { dl = emp.DeductionJson ? JSON.parse(emp.DeductionJson) : []; } catch {}
    }
    let effAl = getEffectiveAllowances(al, selectedMonth, selectedYear);
    let effDl = getEffectiveDeductions(dl, selectedMonth, selectedYear);

    if (effAl.length === 0) {
      effAl = reconstructAllowancesFromRecord(record, allowanceMasterList);
    }
    if (effDl.length === 0) {
      effDl = reconstructDeductionsFromRecord(record, deductionMasterList);
    }

    setRawAllowances(effAl);
    setRawDeductions(effDl);
    
    // Populate form with saved record data
    setF(p => ({
      ...p,
      F_EmployeeMaster: record.F_EmployeeMaster,
      EmployeeName: emp?.Name || record.EmployeeName || "",
      FatherName: emp?.FatherName || record.FatherName || "",
      Department: emp?.DepartmentName || emp?.Department || record.Department || "",
      JoinDate: emp?.JoinDate ? String(emp.JoinDate).substring(0, 10) : (record.JoinDate ? String(record.JoinDate).substring(0, 10) : ""),
      BasicSalary: parseFloat(record.BasicSalary) || 0,
      HRA: parseFloat(record.HRA) || 0,
      Conveyance: parseFloat(record.Conveyance) || 0,
      CCA: parseFloat(record.CCA) || 0,
      Medical: parseFloat(record.Medical) || 0,
      Retention: parseFloat(record.Retention) || 0,
      MonthDays: parseFloat(record.MonthDays) || 31,
      WorkingDays: parseFloat(record.WorkingDays) || 0,
      WorkingHours: parseFloat(record.WorkingHours) || 0,
      WorkingMinutes: parseFloat(record.WorkingMinutes) || 0,
      LeaveWages: parseFloat(record.LeaveWages) || 0,
      FestivalHoliday: parseFloat(record.FestivalHoliday) || 0,
      WeeklyHoliday: parseFloat(record.WeeklyHoliday) || 0,
      CL: parseFloat(record.CL) || 0,
      ML: parseFloat(record.ML) || 0,
      OvertimeHours: parseFloat(record.OvertimeHours) || 0,
      Arrear: parseFloat(record.Arrear) || 0,
      AdvanceBonus: parseFloat(record.AdvanceBonus) || 0,
      ExtraPay: parseFloat(record.ExtraPay) || 0,
      ExtraPay1: parseFloat(record.ExtraPay1) || 0,
      PFRefund: parseFloat(record.PFRefund) || 0,
      Recovery: parseFloat(record.Recovery) || 0,
      Advance: parseFloat(record.Advance) || 0,
      TDS: parseFloat(record.TDS) || 0,
      PFReason: record.PFReason || "",
      PFLast: record.PFLast || "",
      PL_Bal: parseFloat(record.PL_Bal) || 0,
      CL_Bal: parseFloat(record.CL_Bal) || 0,
      ML_Bal: parseFloat(record.ML_Bal) || 0,
      PL_Enjoy: parseFloat(record.PL_Enjoy) || 0,
      CL_Enjoy: parseFloat(record.CL_Enjoy) || 0,
      ML_Enjoy: parseFloat(record.ML_Enjoy) || 0,
      Wages: parseFloat(record.Wages) || 0,
      OverTime: parseFloat(record.OverTime) || 0,
      EmplrPF: parseFloat(record.EmplrPF) || 0,
      Pension: parseFloat(record.Pension) || 0,
      PF: parseFloat(record.PF) || 0,
      ArrearPF: parseFloat(record.ArrearPF) || 0,
      ArrERPF: parseFloat(record.ArrERPF) || 0,
      ArrPens: parseFloat(record.ArrPens) || 0,
      EmplrESI: parseFloat(record.EmplrESI) || 0,
      ESI: parseFloat(record.ESI) || 0,
      Bonus: parseFloat(record.Bonus) || 0,
      Gratuity: parseFloat(record.Gratuity) || 0,
      AdminCharges: parseFloat(record.AdminCharges) || 0,
      AC21: parseFloat(record.AC21) || 0,
      AC22: parseFloat(record.AC22) || 0,
      PFApplicable: record.PFApplicable ?? emp?.PFApplicable ?? false,
      ESIApplicable: record.ESIApplicable ?? emp?.ESIApplicable ?? false,
      PensionApplicable: record.PensionApplicable ?? emp?.PensionApplicable ?? true,
      PaidDay: parseFloat(record.PaidDay) || 0,
      TotalAllowances: parseFloat(record.TotalAllowances) || 0,
      TotalDeductions: parseFloat(record.TotalDeductions) || 0,
      GrossAmount: parseFloat(record.GrossAmount) || 0,
      TotalDeduction: parseFloat(record.TotalDeduction) || 0,
      NetAmount: parseFloat(record.NetAmount) || 0,
      LeaveWithWages: parseFloat(record.LeaveWithWages) || 0,
      LeaveFrom1: record.LeaveFrom1 || "",
      LeaveTo1: record.LeaveTo1 || "",
      LeaveFrom2: record.LeaveFrom2 || "",
      LeaveTo2: record.LeaveTo2 || "",
      LeaveFrom3: record.LeaveFrom3 || "",
      LeaveTo3: record.LeaveTo3 || "",
      AbsentDays: parseFloat(record.AbsentDays) || 0,
      AbsentFrom1: record.AbsentFrom1 || "",
      AbsentTo1: record.AbsentTo1 || "",
      AbsentFrom2: record.AbsentFrom2 || "",
      AbsentTo2: record.AbsentTo2 || "",
      AbsentFrom3: record.AbsentFrom3 || "",
      AbsentTo3: record.AbsentTo3 || "",
      // ── Restore BasicType name from salary master ──
      BasicTypeName: (() => {
        try {
          const salList = emp?.SalaryJson ? JSON.parse(emp.SalaryJson) : [];
          const salEntry = getEffectiveSalary(salList, selectedMonth, selectedYear);
          const bt = basicTypeMaster.find(b => b.Id === (record.F_BasicTypeMaster || salEntry?.F_BasicTypeMaster));
          return bt ? bt.Name : "";
        } catch { return ""; }
      })(),
      // ── Restore saved Allowance / Deduction name + amount fields ──
      ...(() => {
        const dynFields = {};
        for (let i = 1; i <= 10; i++) {
          dynFields[`Allowance${i}Name`]       = record[`Allowance${i}Name`]       || "";
          dynFields[`OriginalAllowance${i}`]   = parseFloat(record[`OriginalAllowance${i}`])   || 0;
          dynFields[`CalculatedAllowance${i}`] = parseFloat(record[`CalculatedAllowance${i}`]) || 0;
          dynFields[`Deduction${i}Name`]       = record[`Deduction${i}Name`]       || "";
          dynFields[`OriginalDeduction${i}`]   = parseFloat(record[`OriginalDeduction${i}`])   || 0;
          dynFields[`CalculatedDeduction${i}`] = parseFloat(record[`CalculatedDeduction${i}`]) || 0;
        }
        return dynFields;
      })(),
    }));
    
    setState(p => ({ ...p, selectedEmployee: emp || { Id: record.F_EmployeeMaster, Name: record.EmployeeName } }));
    // Restore leaveValues from saved record: CL → first leave, ML → second leave
    setLeaveValues(prev => {
      const restored = { ...prev };
      const leaveKeys = Object.keys(prev);
      if (leaveKeys[0] !== undefined) restored[leaveKeys[0]] = parseFloat(record.CL || 0);
      if (leaveKeys[1] !== undefined) restored[leaveKeys[1]] = parseFloat(record.ML || 0);
      return restored;
    });
    setActiveTab('entry'); // Switch to entry tab
  };

  // Recalculate MonthDays based on selectedMonth/Year, Sunday Include, and Employee Weekly Holiday
  useEffect(() => {
    if (selectedRecordId !== null) return; // Do not overwrite saved record's MonthDays
    if (!selectedMonth || !selectedYear) return;

    const empWeeklyHoliday = state.selectedEmployee?.WeeklyHoliday || "";
    const calculatedMonthDays = getCalculatedMonthDays(selectedMonth, selectedYear, payrollParams, empWeeklyHoliday);
    setF(p => ({
      ...p,
      MonthDays: calculatedMonthDays,
      WeeklyHoliday: 0
    }));
  }, [selectedMonth, selectedYear, payrollParams, selectedRecordId, state.selectedEmployee]);

  // Auto-recalculate whenever any value that affects gross/allowances/PF/ESI changes
  useEffect(() => {
    setF(p => {
      const workingDays = computeWorkingDays(p.MonthDays, payrollParams);
      const workingHoursDisplay = getWorkingHoursDisplay(p.WorkingHours, p.WorkingMinutes, payrollParams);
      const totalLeaveAuto = Object.values(leaveValues).reduce((s, v) => s + (parseFloat(v) || 0), 0);
      const paidDays  = Math.min(p.MonthDays, p.WorkingDays + p.LeaveWages + p.FestivalHoliday + p.WeeklyHoliday + totalLeaveAuto);
      
      // Get effective salary entry for selected month/year
      const salList2 = (() => { try { return JSON.parse(state.selectedEmployee?.SalaryJson || "[]"); } catch { return []; } })();
      const salEntry = getEffectiveSalary(salList2, selectedMonth, selectedYear);
      // monthlyBasic = monthly equivalent for % allowance base (daily 500 → 500*26=13000)
      const monthlyBasic = computeBasic(salEntry, workingDays, payrollParams);
      // wages = actual earned wages: Monthly→ rate/monthDays×paidDays | Daily→ rate×paidDays
      const wages = computeWages(salEntry, paidDays, p.MonthDays, p.WorkingHours, p.WorkingMinutes, payrollParams);
      const otAmount = computeOvertime(salEntry, p.OvertimeHours, p.MonthDays, payrollParams);
      
      const totalAl   = computeAllowancesTotal(rawAllowances, p.MonthDays, paidDays, monthlyBasic, selectedMonth, selectedYear);
      const totalDed  = computeDeductionsTotal(rawDeductions, p.MonthDays, paidDays, monthlyBasic, selectedMonth, selectedYear);
      const pfAlAmt   = computePFAllowancesAmount(rawAllowances, p.MonthDays, paidDays, monthlyBasic, selectedMonth, selectedYear);
      const esiAlAmt  = computeESIAllowancesAmount(rawAllowances, p.MonthDays, paidDays, monthlyBasic, selectedMonth, selectedYear);
      const bonus = computeBonus(wages, bonusRate, paidDays);
      const gratuity = computeGratuity(monthlyBasic, gratuityRate, 1);
      // ── PF Base: wages + each component if its flag is ON + allowances (via pfAlAmt inside computePF)
      const pfBase = wages
        + (payrollParams?.IsPFOnOT      ? otAmount  : 0)
        + (payrollParams?.IsPFOnArrear  ? p.Arrear  : 0)
        + (payrollParams?.IsPFOnExtraPay  ? p.ExtraPay  : 0)
        + (payrollParams?.IsPFOnExtraPay1 ? p.ExtraPay1 : 0);
      // ── ESI Base: wages + each component if its flag is ON + allowances (esiAlAmt) + bonus
      const esiBase = wages
        + esiAlAmt
        + (payrollParams?.IsESIOnOT      ? otAmount  : 0)
        + (payrollParams?.IsESIOnArrear  ? p.Arrear  : 0)
        + (payrollParams?.IsESIOnExtraPay  ? p.ExtraPay  : 0)
        + (payrollParams?.IsESIOnExtraPay1 ? p.ExtraPay1 : 0)
        + bonus;
      const gross = wages + p.ExtraPay + p.ExtraPay1 + p.AdvanceBonus + p.Arrear + p.LeaveWages + otAmount + totalAl + bonus;
      // Apply PF only if employee PFApplicable; pfAlAmt (allowances with IsPFApply) added inside computePF
      const { PF, EmplrPF, Pension, AdminCharges, AC21, AC22 } = p.PFApplicable
        ? computePF(pfBase, pfRate, pfAlAmt, p.PensionApplicable)
        : { PF: 0, EmplrPF: 0, Pension: 0, AdminCharges: 0, AC21: 0, AC22: 0 };
      // Apply ESI only if employee ESIApplicable
      const { ESI, EmplrESI } = p.ESIApplicable
        ? computeESI(esiBase, esiRate, payrollParams)
        : { ESI: 0, EmplrESI: 0 };
      const deduct = PF + ESI + p.Recovery + p.TDS + totalDed;
      return { 
        ...p, 
        Wages: wages,
        OverTime: otAmount,
        PaidDay: paidDays,
        WorkingHoursDisplay: workingHoursDisplay,
        TotalAllowances: totalAl,
        TotalDeductions: totalDed,
        GrossAmount: gross, 
        PF, EmplrPF, Pension, ESI, EmplrESI, 
        AdminCharges, AC21, AC22,
        Bonus: bonus, Gratuity: gratuity,
        TotalDeduction: deduct, 
        NetAmount: gross - deduct + p.PFRefund 
      };
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    rawAllowances, rawDeductions, pfRate, esiRate, bonusRate, gratuityRate, payrollParams, state.selectedEmployee,
    selectedMonth, selectedYear,
    f.BasicSalary, f.MonthDays,
    f.WorkingDays, f.WorkingHours, f.WorkingMinutes, f.LeaveWages, f.FestivalHoliday, f.WeeklyHoliday,
    f.ExtraPay, f.ExtraPay1, f.AdvanceBonus, f.Arrear, f.OvertimeHours,
    f.Advance, f.Recovery, f.TDS, f.ArrearPF, f.ArrERPF, f.ArrPens, f.PFRefund,
    leaveValues,
  ]);

  const calculate = () => {
    setF(p => {
      const workingDays = computeWorkingDays(p.MonthDays, payrollParams);
      const workingHoursDisplay = getWorkingHoursDisplay(p.WorkingHours, p.WorkingMinutes, payrollParams);
      const totalLeave = Object.values(leaveValues).reduce((s, v) => s + (parseFloat(v) || 0), 0);
      const paidDays  = Math.min(p.MonthDays, p.WorkingDays + p.LeaveWages + p.FestivalHoliday + p.WeeklyHoliday + totalLeave);
      // Resolve effective salary entry for selected month/year
      const calSalList = (() => { try { return JSON.parse(state.selectedEmployee?.SalaryJson || "[]"); } catch { return []; } })();
      const calSalEntry = getEffectiveSalary(calSalList, selectedMonth, selectedYear);
      // monthlyBasic = monthly equivalent for % allowance base (daily 500 → 500*26=13000)
      const monthlyBasic = computeBasic(calSalEntry, workingDays, payrollParams);
      // wages = actual earned wages based on BasicType + paid days
      // Monthly: rate / MonthDays * PaidDays  |  Daily: rate * PaidDays  |  Hourly: rate * hours
      const calWages = computeWages(calSalEntry, paidDays, p.MonthDays, p.WorkingHours, p.WorkingMinutes, payrollParams);
      const totalAl   = computeAllowancesTotal(rawAllowances, p.MonthDays, paidDays, monthlyBasic, selectedMonth, selectedYear);
      const totalDed  = computeDeductionsTotal(rawDeductions, p.MonthDays, paidDays, monthlyBasic, selectedMonth, selectedYear);
      const pfAlAmt   = computePFAllowancesAmount(rawAllowances, p.MonthDays, paidDays, monthlyBasic, selectedMonth, selectedYear);
      const esiAlAmt  = computeESIAllowancesAmount(rawAllowances, p.MonthDays, paidDays, monthlyBasic, selectedMonth, selectedYear);
      const bonus = computeBonus(calWages, bonusRate, paidDays);
      const gratuity = computeGratuity(monthlyBasic, gratuityRate, 1);
      const otAmount = computeOvertime(calSalEntry, p.OvertimeHours, p.MonthDays, payrollParams);
      // ── PF Base: earned wages + each component if flag ON (allowances added inside computePF)
      const pfBase = calWages
        + (payrollParams?.IsPFOnOT       ? otAmount   : 0)
        + (payrollParams?.IsPFOnArrear   ? p.Arrear   : 0)
        + (payrollParams?.IsPFOnExtraPay  ? p.ExtraPay  : 0)
        + (payrollParams?.IsPFOnExtraPay1 ? p.ExtraPay1 : 0);
      // ── ESI Base: earned wages + each component if flag ON + allowances + bonus
      const esiBase = calWages
        + esiAlAmt
        + (payrollParams?.IsESIOnOT       ? otAmount   : 0)
        + (payrollParams?.IsESIOnArrear   ? p.Arrear   : 0)
        + (payrollParams?.IsESIOnExtraPay  ? p.ExtraPay  : 0)
        + (payrollParams?.IsESIOnExtraPay1 ? p.ExtraPay1 : 0)
        + bonus;
      // Gross = earned wages + all additions
      const gross = calWages + p.ExtraPay + p.ExtraPay1 + p.AdvanceBonus + p.Arrear + p.LeaveWages + otAmount + totalAl + bonus;
      // Apply PF only if employee PFApplicable; pfAlAmt (allowances with IsPFApply) added inside computePF
      const { PF, EmplrPF, Pension, AdminCharges, AC21, AC22 } = p.PFApplicable
        ? computePF(pfBase, pfRate, pfAlAmt, p.PensionApplicable)
        : { PF: 0, EmplrPF: 0, Pension: 0, AdminCharges: 0, AC21: 0, AC22: 0 };
      // Apply ESI only if employee ESIApplicable
      const { ESI, EmplrESI } = p.ESIApplicable
        ? computeESI(esiBase, esiRate, payrollParams)
        : { ESI: 0, EmplrESI: 0 };
      const deduct = PF + ESI + p.Recovery + p.TDS + totalDed;
      return { 
        ...p, 
        Wages: calWages,
        PaidDay: paidDays,
        WorkingHoursDisplay: workingHoursDisplay,
        TotalAllowances: totalAl,
        TotalDeductions: totalDed,
        OverTime: otAmount,
        GrossAmount: gross, 
        PF, EmplrPF, Pension, ESI, EmplrESI, 
        AdminCharges, AC21, AC22,
        Bonus: bonus, Gratuity: gratuity,
        TotalDeduction: deduct, 
        NetAmount: gross - deduct + p.PFRefund 
      };
    });
  };

  // Helper function to format date as YYYY-MM-DD (SQL Server date format, date only no time)
  const formatDateForSave = (dateValue) => {
    if (!dateValue) return "";
    // If it's already a string in YYYY-MM-DD format, return as-is
    if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
      return dateValue;
    }
    // If it's a date object or ISO string, extract just the date part
    const dateObj = new Date(dateValue);
    if (isNaN(dateObj.getTime())) return ""; // Invalid date
    return dateObj.toISOString().split('T')[0]; // Returns YYYY-MM-DD
  };

  const handleSave = async () => {
    if (!f.F_EmployeeMaster) { toast.error("Please select an employee"); return; }
    
    // Check if employee already has a saved record for this month/year (unless editing)
    if (selectedRecordId === null) {
      const exists = savedRecords.some(r => String(r.F_EmployeeMaster) === String(f.F_EmployeeMaster));
      if (exists) {
        toast.error("Payroll record for this employee already exists in the selected month!");
        return;
      }
    }

    setState(p => ({ ...p, isLoading: true }));
    try {
      const fd = new FormData();
      fd.append("Id", selectedRecordId || 0);
      // Append each field separately (exclude display-only fields)
      fd.append("F_FirmMaster", sessionFirm?.Id || 0);
      fd.append("PayrollYear", selectedYear || 0);
      fd.append("PayrollMonth", selectedMonth || "");
      fd.append("F_EmployeeMaster", f.F_EmployeeMaster || 0);
      fd.append("BasicSalary", f.BasicSalary || 0);
      fd.append("HRA", f.HRA || 0);
      fd.append("Conveyance", f.Conveyance || 0);
      fd.append("CCA", f.CCA || 0);
      fd.append("Medical", f.Medical || 0);
      fd.append("Retention", f.Retention || 0);
      fd.append("MonthDays", f.MonthDays || 31);
      fd.append("WorkingDays", f.WorkingDays || 0);
      fd.append("WorkingHours", f.WorkingHours || 0);
      fd.append("WorkingMinutes", f.WorkingMinutes || 0);
      fd.append("LeaveWages", f.LeaveWages || 0);
      fd.append("FestivalHoliday", f.FestivalHoliday || 0);
      fd.append("WeeklyHoliday", f.WeeklyHoliday || 0);
      // Map first leave → CL, second leave → ML (LeaveMaster order)
      const leaveArr = leaveMasterList.map(l => ({ id: l.Id, name: l.Name, val: parseFloat(leaveValues[l.Id] || 0) }));
      fd.append("CL", leaveArr[0]?.val || 0);  // First leave → CL
      fd.append("ML", leaveArr[1]?.val || 0);  // Second leave → ML
      fd.append("OvertimeHours", f.OvertimeHours || 0);
      fd.append("Arrear", f.Arrear || 0);
      fd.append("AdvanceBonus", f.AdvanceBonus || 0);
      fd.append("ExtraPay", f.ExtraPay || 0);
      fd.append("ExtraPay1", f.ExtraPay1 || 0);
      fd.append("PFRefund", f.PFRefund || 0);
      fd.append("Recovery", f.Recovery || 0);
      fd.append("Advance", f.Advance || 0);
      fd.append("TDS", f.TDS || 0);
      fd.append("PFReason", f.PFReason || "");
      if (f.PFLast) fd.append("PFLast", formatDateForSave(f.PFLast));
      fd.append("PL_Bal", f.PL_Bal || 0);
      fd.append("CL_Bal", f.CL_Bal || 0);
      fd.append("ML_Bal", f.ML_Bal || 0);
      fd.append("PL_Enjoy", f.PL_Enjoy || 0);
      fd.append("CL_Enjoy", f.CL_Enjoy || 0);
      fd.append("ML_Enjoy", f.ML_Enjoy || 0);
      fd.append("Wages", f.Wages || 0);
      fd.append("OverTime", f.OverTime || 0);
      fd.append("EmplrPF", f.EmplrPF || 0);
      fd.append("Pension", f.Pension || 0);
      fd.append("PF", f.PF || 0);
      fd.append("ArrearPF", f.ArrearPF || 0);
      fd.append("ArrERPF", f.ArrERPF || 0);
      fd.append("ArrPens", f.ArrPens || 0);
      fd.append("EmplrESI", f.EmplrESI || 0);
      fd.append("ESI", f.ESI || 0);
      fd.append("Bonus", f.Bonus || 0);
      fd.append("Gratuity", f.Gratuity || 0);
      fd.append("AdminCharges", f.AdminCharges || 0);
      fd.append("AC21", f.AC21 || 0);
      fd.append("AC22", f.AC22 || 0);
      fd.append("PFApplicable", f.PFApplicable || false);
      fd.append("ESIApplicable", f.ESIApplicable || false);
      fd.append("PaidDay", f.PaidDay || 0);
      fd.append("TotalAllowances", f.TotalAllowances || 0);
      fd.append("TotalDeductions", f.TotalDeductions || 0);
      fd.append("GrossAmount", f.GrossAmount || 0);
      fd.append("TotalDeduction", f.TotalDeduction || 0);
      fd.append("NetAmount", f.NetAmount || 0);
      fd.append("LeaveWithWages", f.LeaveWithWages || 0);
      fd.append("IsBasicEditable", payrollParams?.IsBasicEditable ? true : false);
      // Only append date fields if they have values
      if (f.LeaveFrom1) fd.append("LeaveFrom1", formatDateForSave(f.LeaveFrom1));
      if (f.LeaveTo1) fd.append("LeaveTo1", formatDateForSave(f.LeaveTo1));
      if (f.LeaveFrom2) fd.append("LeaveFrom2", formatDateForSave(f.LeaveFrom2));
      if (f.LeaveTo2) fd.append("LeaveTo2", formatDateForSave(f.LeaveTo2));
      if (f.LeaveFrom3) fd.append("LeaveFrom3", formatDateForSave(f.LeaveFrom3));
      if (f.LeaveTo3) fd.append("LeaveTo3", formatDateForSave(f.LeaveTo3));
      fd.append("AbsentDays", f.AbsentDays || 0);
      // Only append absent date fields if they have values
      if (f.AbsentFrom1) fd.append("AbsentFrom1", formatDateForSave(f.AbsentFrom1));
      if (f.AbsentTo1) fd.append("AbsentTo1", formatDateForSave(f.AbsentTo1));
      if (f.AbsentFrom2) fd.append("AbsentFrom2", formatDateForSave(f.AbsentFrom2));
      if (f.AbsentTo2) fd.append("AbsentTo2", formatDateForSave(f.AbsentTo2));
      if (f.AbsentFrom3) fd.append("AbsentFrom3", formatDateForSave(f.AbsentFrom3));
      if (f.AbsentTo3) fd.append("AbsentTo3", formatDateForSave(f.AbsentTo3));

      // --- Add Original and Calculated values for Basic and Allowances ---
      const totalLeavedays = Object.values(leaveValues).reduce((s, v) => s + (parseFloat(v) || 0), 0);
      const paidDaysForCalc = Math.min(
        parseFloat(f.MonthDays) || 0,
        (parseFloat(f.WorkingDays) || 0) +
        (parseFloat(f.LeaveWages) || 0) +
        (parseFloat(f.FestivalHoliday) || 0) +
        (parseFloat(f.WeeklyHoliday) || 0) +
        totalLeavedays
      );

      fd.append("Original_BasicSalary", f.BasicSalary || 0);
      fd.append("Calculated_BasicSalary", f.Wages || 0);

      const effectiveAllowances = getEffectiveAllowances(rawAllowances, selectedMonth, selectedYear);
      for (let i = 0; i < 10; i++) {
        const a = effectiveAllowances[i];
        if (a) {
          const name = allowanceMasterList.find(m => String(m.Id) === String(a.F_AllowanceMaster))?.Name || `Allow_${a.F_AllowanceMaster}`;
          const computed = computeSingleAllowance(a, f.MonthDays, paidDaysForCalc, f.BasicSalary);
          const original = parseFloat(a.Amount) || 0;
          fd.append(`Allowance${i+1}Name`, name);
          fd.append(`OriginalAllowance${i+1}`, original);
          fd.append(`CalculatedAllowance${i+1}`, computed);
        } else {
          fd.append(`Allowance${i+1}Name`, "");
          fd.append(`OriginalAllowance${i+1}`, 0);
          fd.append(`CalculatedAllowance${i+1}`, 0);
        }
      }

      const effectiveDeductions = getEffectiveDeductions(rawDeductions, selectedMonth, selectedYear);
      for (let i = 0; i < 10; i++) {
        const d = effectiveDeductions[i];
        if (d) {
          const name = deductionMasterList.find(m => String(m.Id) === String(d.F_DeductionMaster))?.Name || `Deduct_${d.F_DeductionMaster}`;
          const computed = computeSingleDeduction(d, f.MonthDays, paidDaysForCalc, f.BasicSalary);
          const original = parseFloat(d.Amount) || 0;
          fd.append(`Deduction${i+1}Name`, name);
          fd.append(`OriginalDeduction${i+1}`, original);
          fd.append(`CalculatedDeduction${i+1}`, computed);
        } else {
          fd.append(`Deduction${i+1}Name`, "");
          fd.append(`OriginalDeduction${i+1}`, 0);
          fd.append(`CalculatedDeduction${i+1}`, 0);
        }
      }
      // ------------------------------------------------------------------

      fd.append("UserId", 0);
      const res = await Fn_AddEditData(dispatch, () => {}, { arguList: { id: selectedRecordId || 0, formData: fd } }, 'PayrollTransaction/0/token', true, "memberid", navigate, "#");
      if (res && res?.id > 0) { 
        toast.success(selectedRecordId ? "Payroll updated!" : "Payroll saved!", { autoClose: 3000 });
        resetForm(); // Clear form after save to prepare for next entry
        logPayrollSession();
      }
      else { toast.error("Save failed."); }
    } catch { toast.error("Error saving payroll."); }
    finally { setState(p => ({ ...p, isLoading: false })); }
  };

  const resetForm = () => {
    setF({
      F_EmployeeMaster: "", EmployeeName: "", FatherName: "", Department: "", JoinDate: "",
      BasicSalary: 0, HRA: 0, Conveyance: 0, CCA: 0, Medical: 0, Retention: 0,
      MonthDays: 31,
      WorkingDays: 0, WorkingHours: 0, WorkingMinutes: 0, LeaveWages: 0, FestivalHoliday: 0, WeeklyHoliday: 0, OvertimeHours: 0,
      Arrear: 0, AdvanceBonus: 0, ExtraPay: 0, ExtraPay1: 0,
      PFRefund: 0, Recovery: 0, Advance: 0, TDS: 0, PFReason: "", PFLast: "",
      PL_Bal: 0, CL_Bal: 0, ML_Bal: 0, PL_Enjoy: 0, CL_Enjoy: 0, ML_Enjoy: 0,
      Wages: 0, OverTime: 0, EmplrPF: 0, Pension: 0, PF: 0, ArrearPF: 0, ArrERPF: 0, ArrPens: 0, EmplrESI: 0, ESI: 0,
      Bonus: 0, Gratuity: 0,
      AdminCharges: 0, AC21: 0, AC22: 0,
      PFApplicable: false, ESIApplicable: false,
      PaidDay: 0, TotalAllowances: 0, TotalDeductions: 0, GrossAmount: 0, TotalDeduction: 0, NetAmount: 0,
      LeaveWithWages: 0,
      LeaveFrom1: "", LeaveTo1: "", LeaveFrom2: "", LeaveTo2: "", LeaveFrom3: "", LeaveTo3: "",
      AbsentDays: 0,
      AbsentFrom1: "", AbsentTo1: "", AbsentFrom2: "", AbsentTo2: "", AbsentFrom3: "", AbsentTo3: "",
      ...getInitialDynFields(),
    });
    setRawAllowances([]);
    setRawDeductions([]);
    // Reset all leave values to 0
    setLeaveValues(prev => {
      const reset = {};
      Object.keys(prev).forEach(k => { reset[k] = 0; });
      return reset;
    });
    setState(p => ({ ...p, selectedEmployee: null }));
    setSelectedRecordId(null);
    setActiveTab('entry');
  };

  // Get min and max dates for the current payroll month/year (restricts date picker to current month only)
  const getDateRangeForMonth = () => {
    const monthNum = getMonthIndex(selectedMonth);
    const year = parseInt(selectedYear, 10) || new Date().getFullYear();
    
    const firstDay = new Date(year, monthNum, 1);
    const lastDay = new Date(year, monthNum + 1, 0);
    
    const minDate = firstDay.toISOString().split('T')[0]; // YYYY-MM-DD
    const maxDate = lastDay.toISOString().split('T')[0]; // YYYY-MM-DD
    
    return { minDate, maxDate };
  };

  // ─── Focus employee dropdown on page load or after loading ───────────────
  useEffect(() => {
    if (!state.isLoading && empSelectRef.current) {
      empSelectRef.current.focus();
    }
  }, [state.isLoading]);

  // ─── Escape closes save modal ────────────────────────────────────────────
  useEffect(() => {
    const onEsc = (e) => { if (e.key === 'Escape') setShowSaveModal(false); };
    document.addEventListener('keydown', onEsc);
    return () => document.removeEventListener('keydown', onEsc);
  }, []);

  // ─── Enter-key field navigation (desktop-software style) ────────────────
  // Collects all [data-nav-order] elements currently in DOM, moves focus to next.
  // When the last field is reached → switches to Data Display tab.
  const handleEnterNav = React.useCallback((currentOrder) => {
    const all = Array.from(document.querySelectorAll('[data-nav-order]'))
      .map(el => ({ el, order: parseFloat(el.getAttribute('data-nav-order')) }))
      .filter(x => !isNaN(x.order))
      .sort((a, b) => a.order - b.order);
    const idx = all.findIndex(x => x.order === currentOrder);
    if (idx >= 0 && idx < all.length - 1) {
      const next = all[idx + 1];
      next.el.focus();
      if (typeof next.el.select === 'function') next.el.select();
    } else {
      // Last navigable field → go to Data Display tab
      setActiveTab('display');
      setTimeout(() => {
        const btn = document.querySelector('[data-display-enter]');
        if (btn) btn.focus();
      }, 80);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const I = React.useCallback(({ value, onChange, w = 80, readOnly = false, step, name, style, ...rest }) => (
    <input
      type="number"
      style={{ ...(readOnly ? S.inputRO : S.input), width: w, ...style }}
      value={value}
      onChange={onChange}
      readOnly={readOnly}
      step={step}
      name={name}
      autoFocus={lastField === name}
      {...rest}
    />
  ), [lastField]);

  return (
    <div style={S.page} className="payroll-form">
      {/* ─── Focus highlight styles ─── */}
      <style>{`
        .payroll-form input:focus,
        .payroll-form select:focus {
          background: #fff !important;
          border: 1.5px solid #4C6EF5 !important;
          outline: none !important;
          box-shadow: 0 0 0 3px rgba(76,110,245,0.16) !important;
          border-radius: 6px !important;
        }
        @keyframes py-spin {
          0%   { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes py-pulse {
          0%, 100% { opacity: 1; }
          50%       { opacity: 0.5; }
        }
        @keyframes py-fade-in {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
      `}</style>
      <PageTitle activeMenu="Payroll" motherMenu="Transaction" pageContent="Payroll" />

      {/* ─── Full-Page Loader Overlay ─── */}
      {state.isLoading && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(255,255,255,0.72)',
          backdropFilter: 'blur(3px)',
          zIndex: 9998,
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          animation: 'py-fade-in 0.2s ease',
        }}>
          {/* Spinner */}
          <div style={{
            width: 52, height: 52,
            border: '5px solid #E9ECEF',
            borderTop: '5px solid #4C6EF5',
            borderRadius: '50%',
            animation: 'py-spin 0.75s linear infinite',
            marginBottom: 16,
            boxShadow: '0 4px 16px rgba(76,110,245,0.18)',
          }} />
          <div style={{
            fontSize: 14, fontWeight: 700, color: '#4C6EF5',
            letterSpacing: '0.3px',
            animation: 'py-pulse 1.4s ease-in-out infinite',
          }}>Loading...</div>
          <div style={{ fontSize: 12, color: '#67748E', marginTop: 4 }}>Please wait</div>
        </div>
      )}

      {/* ─── Session Banner (Firm / Month / Year) ─── */}
      {isSessionReady ? (
        <div style={{
          display: "flex", alignItems: "center", gap: "10px",
          background: "linear-gradient(135deg,#4C6EF5,#7950F2)", color: "#fff",
          padding: "6px 14px", fontSize: "12px", fontWeight: 500,
          marginBottom: "8px", borderRadius: "8px", boxShadow: "0 2px 8px rgba(76,110,245,0.2)",
        }}>
          <span style={{ color: "#fff" }}>Firm: {sessionFirm.Name}</span>
          <span style={{ opacity: 0.5, color: "#fff" }}>|</span>
          <span style={{ color: "#fff" }}>Month-Year: {(() => {
            const monthNames = ["January","February","March","April","May","June","July","August","September","October","November","December"];
            const monthNum = monthNames.indexOf(selectedMonth) + 1;
            const monthStr = String(monthNum).padStart(2, '0');
            return `${monthStr}-${selectedYear}`;
          })()}</span>
        </div>
      ) : (
        <div style={{
          background: "#FFF8E1", border: "1px solid #FFD54F",
          padding: "10px 16px", fontSize: "13px", color: "#856404",
          marginBottom: "8px", borderRadius: "8px", textAlign: "center",
        }}>
          ⚠ Payroll form is <strong>disabled</strong>. Please go to <strong>Home</strong> and select Firm, Month &amp; Year first.
        </div>
      )}

      {/* ─── Main Card (disabled when session not ready) ─── */}
      <div style={{ ...S.card, opacity: isSessionReady ? 1 : 0.45, pointerEvents: isSessionReady ? "auto" : "none", userSelect: isSessionReady ? "auto" : "none" }}>
        {/* ─── TABS ─── */}
        <div style={{ display: "flex", padding: "0 10px", paddingTop: "4px", background: "#ffffff", borderBottom: "2px solid #E9ECEF" }}>
          <div style={activeTab === "entry" ? S.tabActive : S.tab} onClick={() => setActiveTab("entry")}>Data Entry</div>
          <div style={activeTab === "display" ? S.tabActive : S.tab} onClick={() => setActiveTab("display")}>Data Display</div>
          <div style={activeTab === "leave" ? S.tabActive : S.tab} onClick={() => setActiveTab("leave")}>Leave Date</div>
        </div>

        <div style={S.cardBody}>

          {/* ╔══════════════════════════════════════════════════════════════╗ */}
          {/* ║  TAB 1 — DATA ENTRY                                        ║ */}
          {/* ╚══════════════════════════════════════════════════════════════╝ */}
          {activeTab === "entry" && (
            <div>
              {/* ─── Top Section: Employee Info ─── */}
              <div style={{ display: "flex", justifyContent: "space-between", gap: "10px" }}>
                <div style={{ flex: 1 }}>
                  <LF label="Emp. Name:" lw={100}>
                    <div style={{ position: "relative", flex: 1, display: "flex", alignItems: "center", gap: 6 }}>
                      {/* ── Multi ESI / PF Filter dropdowns (show only when params ON) ── */}
                      {isMultiESI && esiNoList.length > 0 && (
                        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                          <label style={{ ...S.label, width: 100, textAlign: "right", paddingRight: 4, color: "#2F9E44", fontWeight: 600 }}>ESI No Filter:</label>
                          <select
                            style={{ ...S.select, minWidth: 160 }}
                            value={filterEsiNo}
                            onChange={e => setFilterEsiNo(e.target.value)}
                          >
                            <option value="">-- All ESI Nos --</option>
                            {esiNoList.map(no => (
                              <option key={no} value={no}>{no}</option>
                            ))}
                          </select>
                        </div>
                      )}
                      {isMultiPF && pfNoList.length > 0 && (
                        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                          <label style={{ ...S.label, width: 100, textAlign: "right", paddingRight: 4, color: "#1971C2", fontWeight: 600 }}>PF No Filter:</label>
                          <select
                            style={{ ...S.select, minWidth: 160 }}
                            value={filterPfNo}
                            onChange={e => setFilterPfNo(e.target.value)}
                          >
                            <option value="">-- All PF Nos --</option>
                            {pfNoList.map(no => (
                              <option key={no} value={no}>{no}</option>
                            ))}
                          </select>
                        </div>
                      )}
                      <select
                        ref={empSelectRef}
                        data-nav-order="1"
                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleEnterNav(1); } }}
                        style={{ ...S.select, flex: 1, opacity: state.isLoading ? 0.5 : 1 }}
                        value={f.F_EmployeeMaster}
                        onChange={e => handleEmployeeChange(e.target.value)}
                        disabled={state.isLoading}>
                        <option value="">{state.isLoading ? "Loading employees..." : "--Select--"}</option>
                        {state.employees
                          .filter(e => !savedRecords.some(r => String(r.F_EmployeeMaster) === String(e.Id)))
                          .filter(e => !isMultiESI || !filterEsiNo || (e.ESINo || "").trim() === filterEsiNo)
                          .filter(e => !isMultiPF  || !filterPfNo  || (e.PFNo  || "").trim() === filterPfNo)
                          .map(e => <option key={e.Id} value={e.Id}>{e.Name}</option>)}
                      </select>
                      {state.isLoading && (
                        <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                          <div style={{
                            width: 16, height: 16, border: "2px solid #E9ECEF",
                            borderTop: "2px solid #4C6EF5", borderRadius: "50%",
                            animation: "py-spin 0.8s linear infinite", flexShrink: 0
                          }} />
                          <span style={{ fontSize: 11, color: "#4C6EF5", fontWeight: 500, whiteSpace: "nowrap", animation: "py-pulse 1.2s ease-in-out infinite" }}>Loading...</span>
                        </div>
                      )}
                    </div>
                  </LF>
                  <LF label="Employee :" lw={100}>
                    <input style={{ ...S.inputRO, width: 200 }} value={f.EmployeeName} readOnly />
                    <label style={{ ...S.label, width: 50, textAlign: "right", paddingRight: 4 }}>Father</label>
                    <input style={{ ...S.inputRO, flex: 1 }} value={f.FatherName} readOnly />
                  </LF>
                  <LF label="Department" lw={100}>
                    <input style={{ ...S.inputRO, width: 200 }} value={f.Department} readOnly />
                    <label style={{ ...S.label, width: 70, textAlign: "right", paddingRight: 4 }}>Join Date</label>
                    <input style={{ ...S.inputRO, width: 110 }} value={f.JoinDate} readOnly />
                  </LF>
                </div>

                {/* PL / Leave Balance Box */}
                <div style={{ border: "1px solid #E9ECEF", padding: "6px 10px", background: "#F8FAFF", minWidth: 200, borderRadius: "8px" }}>
                  <table style={{ fontSize: "11px", fontWeight: 600 }}>
                    <thead><tr>
                      <td></td>
                      <td style={{ padding: "0 8px", textAlign: "center" }}>PL</td>
                      <td style={{ padding: "0 8px", textAlign: "center" }}>{leaveMasterList[0]?.Name || "CL"}</td>
                      <td style={{ padding: "0 8px", textAlign: "center" }}>{leaveMasterList[1]?.Name || "ML"}</td>
                    </tr></thead>
                    <tbody>
                      <tr>
                        <td style={{ fontWeight: 700, paddingRight: 6 }}>Bal :</td>
                        <td><input style={{ ...S.input, width: 40, textAlign: "center" }} value={f.PL_Bal} onChange={e => setNum('PL_Bal', e.target.value)} /></td>
                        <td><input style={{ ...S.input, width: 40, textAlign: "center" }} value={f.CL_Bal} onChange={e => setNum('CL_Bal', e.target.value)} /></td>
                        <td><input style={{ ...S.input, width: 40, textAlign: "center" }} value={f.ML_Bal} onChange={e => setNum('ML_Bal', e.target.value)} /></td>
                      </tr>
                      <tr>
                        <td style={{ fontWeight: 700, paddingRight: 6 }}>Enjoy</td>
                        <td><input style={{ ...S.input, width: 40, textAlign: "center" }} value={f.PL_Enjoy} onChange={e => setNum('PL_Enjoy', e.target.value)} /></td>
                        <td><input style={{ ...S.input, width: 40, textAlign: "center" }} value={f.CL_Enjoy} onChange={e => setNum('CL_Enjoy', e.target.value)} /></td>
                        <td><input style={{ ...S.input, width: 40, textAlign: "center" }} value={f.ML_Enjoy} onChange={e => setNum('ML_Enjoy', e.target.value)} /></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* ─── Salary Component Strip ─── */}
              {(() => {
                return (
                  <div style={{ ...S.sectionLine, display: "flex", alignItems: "center", gap: "6px", marginTop: "6px", flexWrap: "wrap" }}>
                    <label style={S.label}>Basic:</label>
                    <I 
                      value={f.BasicSalary} 
                      onChange={e => setNum('BasicSalary', e.target.value)} 
                      w={90} 
                      data-nav-order={(payrollParams?.IsBasicEditable || payrollParams?.IsDirectWagesEntry) ? 2 : undefined} 
                      onKeyDown={e => { if ((payrollParams?.IsBasicEditable || payrollParams?.IsDirectWagesEntry) && e.key === 'Enter') { e.preventDefault(); handleEnterNav(2); } }}
                    />
                    {f.BasicTypeName && (
                      <span style={{ fontSize: 11, color: "#666", marginLeft: 8 }}>({f.BasicTypeName})</span>
                    )}
                    {getEffectiveAllowances(rawAllowances, selectedMonth, selectedYear).map((a, idx) => {
                      const name = allowanceMasterList.find(m => String(m.Id) === String(a.F_AllowanceMaster))?.Name || `Allow ${a.F_AllowanceMaster}`;
                      const rawAmt = parseFloat(a.Amount) || 0;
                      const orderVal = Number((2.01 + idx * 0.01).toFixed(2));
                      return (
                        <React.Fragment key={a.F_AllowanceMaster}>
                          <label style={{ ...S.label, color: "#2F9E44" }}>{name}</label>
                          {(payrollParams?.IsBasicEditable || payrollParams?.IsDirectWagesEntry) ? (
                            <I
                              value={a.Amount || ""}
                              onChange={e => {
                                const newAmt = e.target.value;
                                setRawAllowances(prev => prev.map(item => item.F_AllowanceMaster === a.F_AllowanceMaster ? { ...item, Amount: newAmt } : item));
                              }}
                              w={70}
                              style={{ color: "#2F9E44" }}
                              data-nav-order={orderVal}
                              onKeyDown={ev => { if (ev.key === 'Enter') { ev.preventDefault(); handleEnterNav(orderVal); } }}
                            />
                          ) : (
                            <input style={{ ...S.inputRO, width: 70, color: "#2F9E44" }} value={rawAmt.toFixed(2)} readOnly />
                          )}
                        </React.Fragment>
                      );
                    })}
                    {getEffectiveDeductions(rawDeductions, selectedMonth, selectedYear).map(d => {
                      const name = deductionMasterList.find(m => String(m.Id) === String(d.F_DeductionMaster))?.Name || `Deduct ${d.F_DeductionMaster}`;
                      const rawAmt = parseFloat(d.Amount) || 0;
                      return (
                        <React.Fragment key={d.F_DeductionMaster}>
                          <label style={{ ...S.label, color: "#FA5252" }}>{name}(-)</label>
                          <input style={{ ...S.inputRO, width: 70, color: "#FA5252" }} value={rawAmt.toFixed(2)} readOnly />
                        </React.Fragment>
                      );
                    })}
                  </div>
                );
              })()}

              {/* ─── Month Days ─── */}
              <div style={{ display: "flex", gap: "6px", alignItems: "center", marginBottom: "6px" }}>
                <label style={{ ...S.label, width: 100, textAlign: "right" }}>Monthday:</label>
                <I value={f.MonthDays} onChange={e => setNum('MonthDays', e.target.value)} w={60} />
              </div>

              {/* ─── Main 3-Column Section ─── */}
              <div style={{ display: "flex", gap: "8px" }}>
                {/* Column 1 — Left */}
                <div style={{ minWidth: 220 }}>
                  {/* Working Days row with conditional Hours/Minutes */}
                  <div style={{ display: "flex", gap: "8px", alignItems: "center", marginBottom: "6px" }}>
                    <LF label="Working Days:" lw={120}>
                      <I value={f.WorkingDays} onChange={e => setNum('WorkingDays', e.target.value)} w={60} name="WorkingDays" data-nav-order="4" onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleEnterNav(4); } }} />
                    </LF>
                    {payrollParams?.IsHourlyPayment && (
                      <>
                        <LF label="Hours:" lw={50}>
                          <I value={f.WorkingHours} onChange={e => setNum('WorkingHours', e.target.value)} w={40} data-nav-order="5" onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleEnterNav(5); } }} />
                        </LF>
                        <LF label="Minutes:" lw={50}>
                          <I value={f.WorkingMinutes} onChange={e => setNum('WorkingMinutes', e.target.value)} w={40} data-nav-order="6" onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleEnterNav(6); } }} />
                        </LF>
                      </>
                    )}
                  </div>
                  <LF label="Leave Wages:" lw={120}><I value={f.LeaveWages} onChange={e => setNum('LeaveWages', e.target.value)} w={60} name="LeaveWages" data-nav-order="7" onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleEnterNav(7); } }} /></LF>
                  <LF label="Festival Holiday:" lw={120}><I value={f.FestivalHoliday} onChange={e => setNum('FestivalHoliday', e.target.value)} w={60} name="FestivalHoliday" data-nav-order="8" onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleEnterNav(8); } }} /></LF>
                  <LF label="Weekly Holiday:" lw={120}><I value={f.WeeklyHoliday} onChange={e => setNum('WeeklyHoliday', e.target.value)} w={60} name="WeeklyHoliday" data-nav-order="9" onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleEnterNav(9); } }} /></LF>
                  {/* Dynamic leaves from LeaveMaster */}
                  {leaveMasterList.length > 0 ? (
                    leaveMasterList.map((leave, idx) => (
                      <LF key={leave.Id} label={`${leave.Name}:`} lw={120}>
                        <I
                          value={leaveValues[leave.Id] ?? ""}
                          onChange={e => {
                            const val = e.target.value;
                            setLeaveValues(prev => ({ ...prev, [leave.Id]: val }));
                          }}
                          w={60}
                          name={`Leave_${leave.Id}`}
                          data-nav-order={10 + idx}
                          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleEnterNav(10 + idx); } }}
                        />
                      </LF>
                    ))
                  ) : (
                    // Fallback if no leaves defined
                    <LF label="CL:" lw={120}><span style={{ fontSize: 11, color: '#aaa' }}>No leaves defined</span></LF>
                  )}
                  <LF label="Paid Days:" lw={120}>
                    <input style={{ ...S.inputRO, width: 60, fontWeight: 700, color: "#4C6EF5", textAlign: "center" }}
                      value={Math.min(
                        parseFloat(f.MonthDays) || 0,
                        (parseFloat(f.WorkingDays) || 0) +
                        (parseFloat(f.LeaveWages) || 0) +
                        (parseFloat(f.FestivalHoliday) || 0) +
                        (parseFloat(f.WeeklyHoliday) || 0) +
                        Object.values(leaveValues).reduce((s, v) => s + (parseFloat(v) || 0), 0)
                      )}
                      readOnly />
                  </LF>
                  {payrollParams?.IsOvertime && (
                    <LF label="Overtime(Hrs.):" lw={120}><I value={f.OvertimeHours} onChange={e => setNum('OvertimeHours', e.target.value)} w={80} step="0.01" name="OvertimeHours" data-nav-order="12" onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleEnterNav(12); } }} /></LF>
                  )}
                </div>

                {/* Column 2 — Middle */}
                <div style={{ minWidth: 200 }}>
                  {payrollParams?.IsArrearApplicable && (
                    <LF label="Arrear :" lw={90}><I value={f.Arrear} onChange={e => setNum('Arrear', e.target.value)} w={80} name="Arrear" data-nav-order="13" onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleEnterNav(13); } }} /></LF>
                  )}
                  <div style={{ height: 27 }} />
                  {payrollParams?.IsRecovery && (
                    <LF label="Recovery:" lw={90}><I value={f.Recovery} onChange={e => setNum('Recovery', e.target.value)} w={80} name="Recovery" data-nav-order="14" onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleEnterNav(14); } }} /></LF>
                  )}
                  {payrollParams?.IsAdvance && (
                    <LF label="Advance:" lw={90}><I value={f.Advance} onChange={e => setNum('Advance', e.target.value)} w={80} name="Advance" data-nav-order="15" onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleEnterNav(15); } }} /></LF>
                  )}
                  {payrollParams?.IsTDS && (
                    <LF label="TDS:" lw={90}><I value={f.TDS} onChange={e => setNum('TDS', e.target.value)} w={80} name="TDS" data-nav-order="16" onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleEnterNav(16); } }} /></LF>
                  )}
                </div>

                {/* Column 3 — Right */}
                <div style={{ minWidth: 220 }}>
                  {payrollParams?.IsAdvanceBonus && (
                    <LF label="Advance Bonus:" lw={110}><I value={f.AdvanceBonus} onChange={e => setNum('AdvanceBonus', e.target.value)} w={80} name="AdvanceBonus" data-nav-order="17" onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleEnterNav(17); } }} /></LF>
                  )}
                  {payrollParams?.IsExtraPay && (
                    <LF label={payrollParams?.ExtraPayCaption || "Extra Pay"} lw={110}><I value={f.ExtraPay} onChange={e => setNum('ExtraPay', e.target.value)} w={80} name="ExtraPay" data-nav-order="18" onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleEnterNav(18); } }} /></LF>
                  )}
                  {payrollParams?.IsPFAdvRefund && (
                    <LF label="PF Refund:" lw={110}><I value={f.PFRefund} onChange={e => setNum('PFRefund', e.target.value)} w={80} name="PFRefund" data-nav-order="19" onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleEnterNav(19); } }} /></LF>
                  )}
                  {payrollParams?.IsExtraPay1 && (
                    <LF label={payrollParams?.ExtraPay1Caption || "Extra Pay1"} lw={110}><I value={f.ExtraPay1} onChange={e => setNum('ExtraPay1', e.target.value)} w={80} name="ExtraPay1" data-nav-order="20" onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleEnterNav(20); } }} /></LF>
                  )}
                </div>
              </div>

              {/* ─── PF Reason / PF Last ─── */}
              <div style={{ display: "flex", gap: "10px", alignItems: "center", marginTop: "10px", paddingTop: "8px", borderTop: "1px solid #E9ECEF" }}>
                <label style={{ ...S.label, width: 80, textAlign: "right" }}>PF Reason:</label>
                <select style={{ ...S.select, width: 200 }} value={f.PFReason} onChange={e => set('PFReason', e.target.value)}>
                  <option value="">--Select--</option>
                </select>
                <div style={{ width: 30 }} />
                <label style={S.label}>PF Last:</label>
                <input style={{ ...S.input, width: 60 }} value={f.PFLast} onChange={e => set('PFLast', e.target.value)} />
                <input style={{ ...S.input, width: 80 }} />

                <div style={{ marginLeft: "auto" }}>
                  <button type="button" style={{ ...S.btn, background: "rgba(47,158,68,0.1)", color: "#2F9E44", border: "1px solid rgba(47,158,68,0.25)", fontWeight: 600, padding: "5px 24px" }} onClick={() => setActiveTab("leave")}>Leave</button>
                </div>
              </div>

              {/* ─── Bottom Buttons ─── */}
              <div style={{ display: "flex", gap: "8px", justifyContent: "flex-start", marginTop: "12px", paddingTop: "8px", borderTop: "1px solid #E9ECEF" }}>
                <button
                  type="button"
                  style={{ ...S.btn, background: "#4C6EF5", color: "#fff", border: "1px solid #4C6EF5", boxShadow: "0 3px 8px rgba(76,110,245,0.3)" }}
                  onClick={() => { calculate(); handleSave(); }}
                  disabled={state.isLoading || !f.F_EmployeeMaster}
                >
                  {state.isLoading ? (selectedRecordId === null ? "Saving..." : "Updating...") : (selectedRecordId === null ? "Add" : "Update")}
                </button>
                <button type="button" style={{ ...S.btn, background: "rgba(250,82,82,0.1)", color: "#FA5252", border: "1px solid rgba(250,82,82,0.25)" }} onClick={resetForm}>Delete</button>
                <button type="button" style={{ ...S.btn }} onClick={resetForm}>Cancel</button>
                <div style={{ flex: 1 }} />
                <button type="button" style={{ ...S.btn, background: "#F8FAFF" }} onClick={() => navigate(-1)}>Quit</button>
              </div>
            </div>
          )}

          {/* ╔══════════════════════════════════════════════════════════════╗ */}
          {/* ║  TAB 2 — DATA DISPLAY                                      ║ */}
          {/* ╚══════════════════════════════════════════════════════════════╝ */}
          {activeTab === "display" && (
            <div>
              {/* Employee row */}
              <div style={{ display: "flex", gap: "8px", alignItems: "center", marginBottom: "4px" }}>
                <label style={{ ...S.label, width: 80, textAlign: "right" }}>Employee</label>
                <input style={{ ...S.inputRO, width: 220 }} value={f.EmployeeName || ""} readOnly />
                <label style={{ ...S.label, width: 90, textAlign: "right" }}>Father Name</label>
                <input style={{ ...S.inputRO, flex: 1 }} value={f.FatherName || ""} readOnly />
              </div>
              <div style={{ display: "flex", gap: "8px", alignItems: "center", marginBottom: "8px" }}>
                <label style={{ ...S.label, width: 80, textAlign: "right" }}>Department</label>
                <input style={{ ...S.inputRO, width: 360 }} value={f.Department || ""} readOnly />
              </div>

              {/* Paid Day */}
              <div style={{ textAlign: "center", marginBottom: "10px" }}>
                <label style={{ ...S.label, marginRight: 10 }}>Paid Day :</label>
                <input type="number" style={{ ...S.inputRO, width: 80, textAlign: "right", fontWeight: 700, color: "#4C6EF5" }} 
                  value={f.PaidDay || 0} readOnly />
              </div>

              {/* 2-Column: Left = deductions, Right = components */}
              <div style={{ display: "flex", gap: "30px" }}>
                {/* Left Column */}
                <div style={{ minWidth: 220 }}>
                  <LF label="Wages :" lw={90}><I value={f.Wages} onChange={e => setNum('Wages', e.target.value)} w={100} /></LF>
                  {payrollParams?.IsOvertime && (
                    <LF label="Over Time :" lw={90}><input style={{ ...S.inputRO, width: 100 }} value={to2Dec(f.OverTime)} readOnly title={`OT: ${f.OvertimeHours || 0} hrs`} /></LF>
                  )}
                  {payrollParams?.IsPF && (
                    <>
                      <LF label="Emplr Pf :" lw={90}><input style={{ ...S.inputRO, width: 100 }} value={to2Dec(f.EmplrPF)} readOnly /></LF>
                      <LF label="Pension :" lw={90}><input style={{ ...S.inputRO, width: 100 }} value={to2Dec(f.Pension)} readOnly /></LF>
                      <LF label="PF :" lw={90}><input style={{ ...S.inputRO, width: 100 }} value={to2Dec(f.PF)} readOnly /></LF>
                      <LF label="ArrearPF :" lw={90}><I value={f.ArrearPF} onChange={e => setNum('ArrearPF', e.target.value)} w={100} /></LF>
                      <LF label="Arr ERPF :" lw={90}><I value={f.ArrERPF} onChange={e => setNum('ArrERPF', e.target.value)} w={100} /></LF>
                      <LF label="Arr Pens. :" lw={90}><I value={f.ArrPens} onChange={e => setNum('ArrPens', e.target.value)} w={100} /></LF>
                    </>
                  )}
                  {payrollParams?.IsESI && (
                    <>
                      <LF label="Emplr Esi :" lw={90}><input style={{ ...S.inputRO, width: 100 }} value={to2Dec(f.EmplrESI)} readOnly /></LF>
                      <LF label="ESI :" lw={90}><input style={{ ...S.inputRO, width: 100 }} value={to2Dec(f.ESI)} readOnly /></LF>
                    </>
                  )}
                  {payrollParams?.IsBonusParameter && (
                    <LF label="Bonus :" lw={90}><input style={{ ...S.inputRO, width: 100 }} value={to2Dec(f.Bonus)} readOnly /></LF>
                  )}
                  {payrollParams?.IsGratuity && (
                    <LF label="Gratuity :" lw={90}><input style={{ ...S.inputRO, width: 100 }} value={to2Dec(f.Gratuity)} readOnly /></LF>
                  )}
                  {payrollParams?.IsPF && (
                    <>
                      {(parseFloat(f.AC22) || 0) > 0 && (
                        <LF label="AC22 :" lw={90}><input style={{ ...S.inputRO, width: 100 }} value={to2Dec(f.AC22)} readOnly title="AC22 (Employer only)" /></LF>
                      )}
                    </>
                  )}
                </div>

                {/* Right Column */}
                {(() => {
                  const totalLeave = Object.values(leaveValues).reduce((s, v) => s + (parseFloat(v) || 0), 0);
                  const paidDays =
                    (parseFloat(f.WorkingDays) || 0) +
                    (parseFloat(f.LeaveWages) || 0) +
                    (parseFloat(f.FestivalHoliday) || 0) +
                    (parseFloat(f.WeeklyHoliday) || 0) +
                    totalLeave;
                  
                  const effectiveAl = getEffectiveAllowances(rawAllowances, selectedMonth, selectedYear);
                  const effectiveDl = getEffectiveDeductions(rawDeductions, selectedMonth, selectedYear);

                  const hasDynAl = Array.from({ length: 10 }).some((_, i) => !!f[`Allowance${i + 1}Name`]);
                  const hasDynDl = Array.from({ length: 10 }).some((_, i) => !!f[`Deduction${i + 1}Name`]);

                  return (
                    <div style={{ minWidth: 220 }}>
                      {effectiveAl.length > 0 ? (
                        effectiveAl.map(a => {
                          const name = allowanceMasterList.find(m => String(m.Id) === String(a.F_AllowanceMaster))?.Name || `Allow ${a.F_AllowanceMaster}`;
                          const computed = computeSingleAllowance(a, f.MonthDays, paidDays, f.BasicSalary);
                          return (
                            <LF key={a.F_AllowanceMaster} label={name} lw={100}>
                              <input style={{ ...S.inputRO, width: 100 }} value={to2Dec(computed)} readOnly />
                            </LF>
                          );
                        })
                      ) : (
                        hasDynAl && Array.from({ length: 10 }).map((_, i) => {
                          const name = f[`Allowance${i + 1}Name`];
                          const computed = parseFloat(f[`CalculatedAllowance${i + 1}`]) || 0;
                          if (!name) return null;
                          return (
                            <LF key={`dyn_al_${i}`} label={name} lw={100}>
                              <input style={{ ...S.inputRO, width: 100 }} value={to2Dec(computed)} readOnly />
                            </LF>
                          );
                        })
                      )}

                      {(effectiveDl.length > 0 || hasDynDl) && (
                        <div style={{ borderTop: "1px dashed #aaa", marginTop: 4, paddingTop: 4 }}>
                          {effectiveDl.length > 0 ? (
                            effectiveDl.map(d => {
                              const name = deductionMasterList.find(m => String(m.Id) === String(d.F_DeductionMaster))?.Name || `Deduct ${d.F_DeductionMaster}`;
                              const computed = computeSingleDeduction(d, f.MonthDays, paidDays, f.BasicSalary);
                              return (
                                <LF key={d.F_DeductionMaster} label={`${name}(-)`} lw={100}>
                                  <input style={{ ...S.inputRO, width: 100, color: "#FA5252" }} value={to2Dec(computed)} readOnly />
                                </LF>
                              );
                            })
                          ) : (
                            Array.from({ length: 10 }).map((_, i) => {
                              const name = f[`Deduction${i + 1}Name`];
                              const computed = parseFloat(f[`CalculatedDeduction${i + 1}`]) || 0;
                              if (!name) return null;
                              return (
                                <LF key={`dyn_dl_${i}`} label={`${name}(-)`} lw={100}>
                                  <input style={{ ...S.inputRO, width: 100, color: "#FA5252" }} value={to2Dec(computed)} readOnly />
                                </LF>
                              );
                            })
                          )}
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>

              {/* ─── Summary Row ─── */}
              <div style={{ display: "flex", alignItems: "center", gap: "15px", marginTop: "14px", paddingTop: "8px", borderTop: "1px solid #E9ECEF", justifyContent: "center", flexWrap: "wrap" }}>
                <span style={S.summaryLabel}>Total Allowances:</span><span style={S.summaryVal}>{to2Dec(f.TotalAllowances)}</span>
                <span style={S.summaryLabel}>Total Deductions:</span><span style={S.summaryVal}>{to2Dec(f.TotalDeductions)}</span>
                <span style={S.summaryLabel}>Gros Amount:</span><span style={S.summaryVal}>{to2Dec(f.GrossAmount)}</span>
                <span style={S.summaryLabel}>Total Deduction :</span><span style={S.summaryVal}>{to2Dec(f.TotalDeduction)}</span>
              </div>
              <div style={{ textAlign: "center", marginTop: "8px" }}>
                <span style={{ ...S.summaryLabel, fontSize: "14px" }}>NET AMOUNT:</span>
                <span style={{ ...S.summaryVal, fontSize: "14px", fontWeight: 700, background: "rgba(76,110,245,0.14)", border: "1px solid rgba(76,110,245,0.3)", color: "#4C6EF5", marginLeft: 10 }}>{to2Dec(f.NetAmount)}</span>
              </div>

              {/* ─── Bottom Buttons ─── */}
              <div style={{ display: "flex", gap: "8px", justifyContent: "flex-start", marginTop: "12px", paddingTop: "8px", borderTop: "1px solid #E9ECEF" }}>
                <button
                  type="button"
                  data-display-enter
                  style={{ ...S.btn, background: "#4C6EF5", color: "#fff", border: "1px solid #4C6EF5", boxShadow: "0 3px 8px rgba(76,110,245,0.3)" }}
                  onClick={() => { calculate(); setShowSaveModal(true); }}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); calculate(); setShowSaveModal(true); } }}
                  disabled={state.isLoading || !f.F_EmployeeMaster}
                >
                  {state.isLoading ? (selectedRecordId === null ? "Saving..." : "Updating...") : (selectedRecordId === null ? "Add" : "Update")}
                </button>
                <button type="button" style={{ ...S.btn, background: "rgba(250,82,82,0.1)", color: "#FA5252", border: "1px solid rgba(250,82,82,0.25)" }} onClick={resetForm}>Delete</button>
                <button type="button" style={{ ...S.btn }} onClick={resetForm}>Cancel</button>
                <div style={{ flex: 1 }} />
                <button type="button" style={{ ...S.btn, background: "#F8FAFF" }} onClick={() => navigate(-1)}>Quit</button>
              </div>
            </div>
          )}

          {/* ╔══════════════════════════════════════════════════════════════╗ */}
          {/* ║  TAB 3 — LEAVE DATE                                        ║ */}
          {/* ╚══════════════════════════════════════════════════════════════╝ */}
          {activeTab === "leave" && (
            <div>
              {/* Leave With Wages */}
              <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "12px", marginTop: "6px" }}>
                <div style={{ border: "1px solid rgba(47,158,68,0.25)", padding: "3px 12px", background: "rgba(47,158,68,0.08)", fontSize: "12px", fontWeight: 600, borderRadius: "6px", color: "#2F9E44" }}>Leave with Wages</div>
                <I value={f.LeaveWithWages} onChange={e => setNum('LeaveWithWages', e.target.value)} w={60} />
              </div>

              {/* Leave From / To - Date only (YYYY-MM-DD format) - Restricted to current payroll month */}
              <div style={{ display: "flex", gap: "30px", marginBottom: "20px" }}>
                <div>
                  <LF label="Leave From 1:" lw={100}><input type="date" style={S.input} value={f.LeaveFrom1} onChange={e => set('LeaveFrom1', e.target.value)} {...getDateRangeForMonth()} /></LF>
                  <LF label="Leave From 2:" lw={100}><input type="date" style={S.input} value={f.LeaveFrom2} onChange={e => set('LeaveFrom2', e.target.value)} {...getDateRangeForMonth()} /></LF>
                  <LF label="Leave From 3:" lw={100}><input type="date" style={S.input} value={f.LeaveFrom3} onChange={e => set('LeaveFrom3', e.target.value)} {...getDateRangeForMonth()} /></LF>
                </div>
                <div>
                  <LF label="Leave To 1:" lw={80}><input type="date" style={S.input} value={f.LeaveTo1} onChange={e => set('LeaveTo1', e.target.value)} {...getDateRangeForMonth()} /></LF>
                  <LF label="Leave To 2:" lw={80}><input type="date" style={S.input} value={f.LeaveTo2} onChange={e => set('LeaveTo2', e.target.value)} {...getDateRangeForMonth()} /></LF>
                  <LF label="Leave To 3:" lw={80}><input type="date" style={S.input} value={f.LeaveTo3} onChange={e => set('LeaveTo3', e.target.value)} {...getDateRangeForMonth()} /></LF>
                </div>
              </div>

              {/* Absent Days */}
              <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "12px" }}>
                <div style={{ border: "1px solid rgba(250,82,82,0.25)", padding: "3px 12px", background: "rgba(250,82,82,0.08)", fontSize: "12px", fontWeight: 600, borderRadius: "6px", color: "#FA5252" }}>Absent Days :</div>
                <I value={f.AbsentDays} onChange={e => setNum('AbsentDays', e.target.value)} w={60} />
              </div>

              {/* Absent From / To - Date only (YYYY-MM-DD format) - Restricted to current payroll month */}
              <div style={{ display: "flex", gap: "30px" }}>
                <div>
                  <LF label="Absent From 1:" lw={110}><input type="date" style={S.input} value={f.AbsentFrom1} onChange={e => set('AbsentFrom1', e.target.value)} {...getDateRangeForMonth()} /></LF>
                  <LF label="Absent From 2:" lw={110}><input type="date" style={S.input} value={f.AbsentFrom2} onChange={e => set('AbsentFrom2', e.target.value)} {...getDateRangeForMonth()} /></LF>
                  <LF label="Absent From 3:" lw={110}><input type="date" style={S.input} value={f.AbsentFrom3} onChange={e => set('AbsentFrom3', e.target.value)} {...getDateRangeForMonth()} /></LF>
                </div>
                <div>
                  <LF label="Absent To 1:" lw={80}><input type="date" style={S.input} value={f.AbsentTo1} onChange={e => set('AbsentTo1', e.target.value)} {...getDateRangeForMonth()} /></LF>
                  <LF label="Absent To 2:" lw={80}><input type="date" style={S.input} value={f.AbsentTo2} onChange={e => set('AbsentTo2', e.target.value)} {...getDateRangeForMonth()} /></LF>
                  <LF label="Absent To 3:" lw={80}><input type="date" style={S.input} value={f.AbsentTo3} onChange={e => set('AbsentTo3', e.target.value)} {...getDateRangeForMonth()} /></LF>
                </div>
              </div>
            </div>
          )}

        </div>
      </div>

      {/* ─── Save Confirmation Modal ─────────────────────────────────────── */}
      {showSaveModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.45)', zIndex: 9999,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{
            background: '#fff', border: '2px solid #1a5276',
            padding: '18px 28px 16px', borderRadius: '4px', minWidth: 300, maxWidth: 380,
            fontFamily: "'Segoe UI', Tahoma, sans-serif", fontSize: 13,
            boxShadow: '0 6px 24px rgba(0,0,0,0.35)',
          }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: '#1a5276', marginBottom: 12, borderBottom: '1px solid #ccc', paddingBottom: 6 }}>
              Confirm Save
            </div>
            <div style={{ marginBottom: 4 }}><b>Employee :</b> {f.EmployeeName || '—'}</div>
            <div style={{ marginBottom: 4 }}><b>Month    :</b> {selectedMonth} {selectedYear}</div>
            <div style={{ marginBottom: 4 }}><b>Paid Days:</b> {f.PaidDay}</div>
            <div style={{ marginBottom: 4 }}><b>Gross    :</b> ₹ {(f.GrossAmount || 0).toFixed(2)}</div>
            <div style={{ marginBottom: 12 }}><b>Net Amt  :</b> <span style={{ fontWeight: 700, color: '#1a5276' }}>₹ {(f.NetAmount || 0).toFixed(2)}</span></div>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
              <button
                autoFocus
                type="button"
                style={{ ...S.btn, background: '#b3e5fc', minWidth: 80, fontWeight: 700 }}
                onClick={() => { handleSave(); setShowSaveModal(false); }}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleSave(); setShowSaveModal(false); } }}
              >
                OK
              </button>
              <button
                type="button"
                style={{ ...S.btn, background: '#ffcdd2', minWidth: 80 }}
                onClick={() => setShowSaveModal(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Arrow Button (Right Side Sticky) ─── */}
      {isSessionReady && savedRecords.length > 0 && (
        <button
          onClick={() => setShowSidePanel(!showSidePanel)}
          style={{
            position: "fixed", right: showSidePanel ? "320px" : "12px", top: "50%", transform: "translateY(-50%)", zIndex: 1001,
            padding: "10px 14px", background: "#4C6EF5", color: "#fff", border: "none",
            borderRadius: "50%", width: "44px", height: "44px", display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: "20px", fontWeight: 600, cursor: "pointer",
            boxShadow: "0 4px 12px rgba(76,110,245,0.4)", transition: "all 0.3s"
          }}
        >
          {showSidePanel ? "◀" : "▶"}
        </button>
      )}

      {/* ─── Side Panel: Saved Records ─── */}
      {isSessionReady && savedRecords.length > 0 && showSidePanel && (
        <div style={{
          position: "fixed", right: 0, top: 0, bottom: 0, width: "320px",
          background: "#ffffff", boxShadow: "-2px 0 12px rgba(0,0,0,0.15)",
          overflowY: "auto", zIndex: 999, padding: "12px", borderLeft: "1px solid #E9ECEF"
        }}>
          <div style={{ fontSize: "13px", fontWeight: 700, color: "#344767", marginBottom: "10px", paddingBottom: "8px", borderBottom: "2px solid #4C6EF5" }}>
            📋 Saved Records ({savedRecords.length})
          </div>
          <input
            type="text"
            placeholder="Search Name / Father..."
            value={panelSearch}
            onChange={(e) => setPanelSearch(e.target.value)}
            style={{
              width: "100%", padding: "8px 10px", marginBottom: "10px", border: "1px solid #E9ECEF",
              borderRadius: "6px", fontSize: "12px", fontWeight: 500, boxSizing: "border-box"
            }}
          />
          {savedRecords.filter(record => {
            const emp = state.employees.find(e => String(e.Id) === String(record.F_EmployeeMaster));
            const searchLower = panelSearch.toLowerCase();
            return (emp?.Name || "").toLowerCase().includes(searchLower) || (emp?.FatherName || "").toLowerCase().includes(searchLower);
          }).map(record => {
            const emp = state.employees.find(e => String(e.Id) === String(record.F_EmployeeMaster));
            const isSelected = selectedRecordId === record.Id;
            return (
              <div
                key={record.Id}
                onClick={() => loadSavedRecord(record)}
                style={{
                  padding: "8px 10px", marginBottom: "6px", borderRadius: "6px",
                  border: isSelected ? "2px solid #4C6EF5" : "1px solid #E9ECEF",
                  background: isSelected ? "rgba(76,110,245,0.1)" : "#F8FAFF",
                  cursor: "pointer", transition: "all 0.15s",
                  fontSize: "12px", fontWeight: 500, color: "#344767"
                }}
              >
                {emp?.Name || "N/A"} | {emp?.FatherName || "N/A"}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Payroll;